export type Lang = 'th' | 'en'

export interface Dict {
  lang: {
    toggle: string
    /** Accessible name for the toggle — the visible text is just a language code. */
    toggleLabel: string
    th: string
    en: string
  }
  nav: {
    kenoLimits: string
    partnerWallet: string
    dashboard: string
    rounds: string
    bets: string
    members: string
    games: string
    settlements: string
    reports: string
    masters: string
    admins: string
    agents: string
    providers: string
    auditLog: string
    ipWhitelist: string
    management: string
    profile: string
    numberLimits: string
    myNumberLimits: string
    numberLimitHistory: string
    payoutRates: string
    settings: string
    banners: string
    keys: string
    docs: string
    draws: string
    staging: string
    stagingTest: string
    stagingRuns: string
    stagingReports: string
  }
  auth: {
    signIn: string
    signInSubtitle: string
    username: string
    password: string
    showPassword: string
    hidePassword: string
    submitting: string
    adminOnly: string
    usernameRequired: string
    passwordRequired: string
    invalidCredentials: string
    networkError: string
  }
  header: {
    history: string
    logout: string
  }
  common: {
    loading: string
    error: string
    retry: string
    save: string
    saving: string
    cancel: string
    close: string
    create: string
    creating: string
    edit: string
    delete: string
    actions: string
    status: string
    createdAt: string
    updatedAt: string
    page: string
    of: string
    prev: string
    next: string
    total: string
    items: string
    noData: string
    confirm: string
    success: string
    refresh: string
    search: string
    filter: string
    all: string
    active: string
    inactive: string
    suspended: string
    yes: string
    no: string
    username: string
    password: string
    displayName: string
    optional: string
    createdShort: string
    adminConsole: string
    masterConsole: string
    agentConsole: string
    balance: string
  }
  dashboard: {
    title: string
    totalMembers: string
    activeMembers: string
    betsToday: string
    betVolume: string
    payouts: string
    houseEdge: string
    openRounds: string
    statsError: string
    maintenanceMode: string
    maintenanceActive: string
  }
  members: {
    title: string
    createMember: string
    username: string
    displayName: string
    role: string
    status: string
    wallet: string
    loginAs: string
    balance: string
    credit: string
    creditAmount: string
    creditNote: string
    creditSuccess: string
    impersonateError: string
    chooseGameTitle: (username: string) => string
    chooseGameDescription: string
    gameLottery: string
    gameLotteryHint: string
    gameKeno: string
    gameKenoHint: string
    walletError: string
    createError: string
    noMembers: string
    confirmImpersonate: (username: string) => string
    createdViaApiNotice: string
    suspend: string
    unsuspend: string
    suspendSuccess: string
    suspendError: string
  }
  betReport: {
    title: string
    member: string
    betCount: string
    betVolume: string
    payout: string
    net: string
    viewBets: string
    memberBets: string
    noActivity: string
    backToReport: string
    betType: string
    result: string
    game: string
    date: string
  }
  numberCaps: {
    title: string
    desc: string
    addNumbers: (digits: number) => string
    addChoice: string
    cap: string
    capPlaceholderHint: (def: string) => string
    set: string
    worstCase: (amount: string) => string
    colNumber: string
    colCap: string
    colVsDefault: string
    colTaken: string
    colLoss: string
    lossHint: string
    lossIfFull: (amount: string) => string
    sortHint: (column: string) => string
    typeDefault: (def: string) => string
    higher: (diff: string) => string
    lower: (diff: string) => string
    same: string
    overPool: (pool: string) => string
    addToType: string
    editing: string
    edit: string
    remove: string
    empty: string
    loadError: string
    badDigits: (digits: number, bad: string) => string
    badChoice: (bad: string) => string
    belowMin: (min: string) => string
    saved: (n: number) => string
    otpTitle: string
    otpConfirm: string
    otpSetDesc: (n: number, list: string) => string
    otpRemoveDesc: (number: string, now: string, def: string) => string
    otpLimitsDesc: (type: string, list: string) => string
  }
  agentNumberRules: {
    title: string
    desc: string
    carriesOver: string
    clearAll: string
    clearTitle: string
    clearDesc: (count: number, lottery: string) => string
    clearConfirm: string
    closeNumbers: string
    setLimit: string
    wholeType: string
    housePayout: string
    typePayout: string
    myPayout: string
    myCap: string
    effCap: string
    effPayout: string
    fromHouse: string
    fromType: string
    myNumbers: string
    addTitle: string
    manageType: string
    editingAbove: string
    typeRuleOnly: string
    mineVsHouse: string
    blocked: string
    block: string
    unblock: string
    blockType: string
    unblockType: string
    addNumbers: (digits: number) => string
    addChoice: string
    addHint: string
    empty: string
    clamped: (asked: string, actual: string) => string
    badDigits: (digits: number, bad: string) => string
    badChoice: (bad: string) => string
  }
  agentSettings: {
    title: string
    maintenanceMode: string
    maintenanceDesc: string
    maintenanceOn: string
    maintenanceOff: string
    toggleSuccess: string
    toggleError: string
    walletApiKeyTitle: string
    walletApiKeyDesc: string
    walletApiKeyLabel: string
    walletApiKeyPlaceholder: string
    walletApiKeyCurrentNotSet: string
    walletApiKeySave: string
    walletApiKeySaving: string
    walletApiKeySaveSuccess: string
    walletApiKeySaveError: string
  }
  agentKeys: {
    title: string
    nexbitApiKey: string
    nexbitApiKeyDesc: string
    launchSecret: string
    launchSecretDesc: string
    rotate: string
    rotating: string
    rotateSuccess: string
    rotateWarning: string
    newKeyNotice: string
    copyNow: string
  }
  agentPassword: {
    title: string
    current: string
    newPassword: string
    confirm: string
    mismatch: string
    tooShort: string
    success: string
    wrong: string
    change: string
    changing: string
  }
  games: {
    title: string
    subtitle: string
    config: string
    unit: string
    gameCode: string
    minBet: string
    maxBet: string
    duration: string
    betClose: string
    cooldown: string
    payoutHigh: string
    payoutLow: string
    payoutOdd: string
    payoutEven: string
    highThreshold: string
    isActive: string
    saveSuccess: string
    saveError: string
    noGames: string
    seconds: string
    secShort: string
    tradingSchedule: string
    roundSec: string
    closeBeforeSec: string
    highX: string
    lowX: string
    oddX: string
    evenX: string
    metaRound: string
    metaClose: string
    metaPayout: string
  }
  draws: {
    title: string
    lotteries: string
    lotteryList: string
    selectLottery: string
    category: string
    categoryDraw: string
    categoryStock: string
    recentDraws: string
    drawNumber: string
    drawDate: string
    status: string
    closeAt: string
    noLotteries: string
    noDraws: string
    statusScheduled: string
    statusOpen: string
    statusClosed: string
    statusResulted: string
    statusSettled: string
    statusCancelled: string
    statusSkipped: string
    skipDraw: string
    skipTitle: string
    skipDesc: string
    skipConfirm: string
    skipDone: (n: number, amount: string) => string
    skipError: string
    cancelDraw: string
    cancelTitle: string
    cancelDesc: string
    cancelReason: string
    cancelReasonHint: string
    cancelConfirm: string
    cancelDone: (n: number, amount: string) => string
    cancelError: string
    bettingOpen: string
    readyForResult: string
    result: string
    enterResult: string
    editResult: string
    settle: string
    voidAction: string
    payout: string
    payoutTitle: string
    payoutDesc: string
    payoutSubmit: string
    payoutError: string
    payoutSaved: string
    payoutOpenWarning: string
    payoutBetType: string
    payoutEnabled: string
    payoutRate: string
    payoutMin: string
    payoutMax: string
    payoutPerNumber: string
    payoutPerType: string
    exposure: string
    exposureTitle: string
    exposureDesc: string
    exposureEmpty: string
    exposureTaken: string
    exposureBets: string
    exposureFull: string
    exposureTopNumbers: string
    exposureAuto: string
    blockTitle: string
    blockPageDesc: string
    limitsTitle: string
    limitsNoConfig: string
    limitsMaxBetHint: string
    limitsPerNumberHint: string
    limitsPerTypeHint: string
    limitsSaved: string
    limitsNoBets: string
    limitsNumber: string
    limitsFill: string
    limitsClosed: string
    limitsLiability: string
    limitsWorstCase: string
    limitsWorstNet: string
    limitsWorstHint: string
    blockNoLiveDraw: string
    blockHint: string
    blockAdd: string
    blockEmpty: string
    blockAllDraws: string
    blockAllBadge: string
    blockRemove: string
    payoutCeiling: (max: number) => string
    payoutInvalid: (types: string) => string
    payoutChanges: string
    payoutPassword: string
    payoutPasswordHint: string
    betTypeLabel: Record<string, string>
    settings: string
    settingsTitle: string
    settingsDesc: string
    settingsError: string
    settingsSaved: string
    rules: string
    rulesTitle: (name: string) => string
    rulesHint: string
    rulesTabEdit: string
    rulesTabPreview: string
    rulesLoadError: string
    rulesSaveError: string
    rulesSaved: string
    scheduleType: string
    scheduleTypeLabel: Record<'daily' | 'weekly' | 'monthly' | 'manual', string>
    scheduleDays: string
    scheduleDates: string
    scheduleDatesHint: string
    scheduleTime: string
    scheduleTimeHint: string
    addLottery: string
    addLotteryDone: (name: string) => string
    scheduleRounds: (n: number) => string
    roundAdd: string
    roundLabelPlaceholder: string
    roundTimeOf: (n: number) => string
    roundLabelOf: (n: number) => string
    roundRemoveOf: (n: number) => string
    roundDuplicate: string
    roundLabelRequired: string
    roundTimeInvalid: string
    roundOpenOf: (n: number) => string
    roundOpenLabel: string
    roundOpenInherit: string
    roundOpenInvalid: string
    roundOpenAfterClose: string
    roundOpenComputed: (duration: string) => string
    roundOpenModeInherit: string
    roundOpenModeTime: string
    roundOpenModeAfterPrev: string
    roundOpenAfterPrevOf: (n: number) => string
    roundOpenAfterPrevHint: string
    roundOpenAfterPrevComputed: (duration: string) => string
    roundOpenAfterPrevNow: string
    roundOpenAfterPrevTooLate: string
    createAfterEnding: string
    createAfterCancelNote: string
    createAfterSkipNote: string
    roundsMax: (n: number) => string
    roundsPreview: (list: string) => string
    roundsChangeNote: string
    round: string
    scheduleDaily: string
    scheduleManual: string
    scheduleManualHint: string
    scheduleMonthlyOn: (dates: string) => string
    weekdayShort: string[]
    closeBefore: string
    closeBeforeHint: string
    openBefore: string
    openBeforeOff: string
    openBeforeDays: string
    openBeforeTime: string
    openBeforeOffHint: string
    openBeforeDaysHint: string
    openBeforePlaceholder: string
    openBeforeTimeHint: string
    openBeforeTimeComputed: (duration: string) => string
    openBeforeTimeRoundNote: (time: string) => string
    openAfterPrevious: string
    openAfterPreviousHint: string
    openAfterPreviousComputed: (duration: string) => string
    closeBeforeShort: (mins: number) => string
    createDraw: string
    createTitle: string
    createDesc: string
    createDescManual: string
    createEditTimes: string
    createSubmit: string
    creating: string
    createError: string
    createSuccess: string
    createOrderInvalid: string
    createBlocked: (drawNumber: number, closeAt: string) => string
    createStartsOpen: string
    createStartsScheduled: string
    openAt: string
    openAtHint: string
    closeAtHint: string
    drawAt: string
    drawAtHint: string
    clockNoDraw: string
    clockAwaitingResult: string
    clockAwaitingPayout: string
    unitDay: string
    unitHour: string
    unitMin: string
    marketOpen: string
    marketClosed: string
    marketToggleError: string
    closeBetting: string
    closeConfirmTitle: string
    closeConfirmDesc: string
    closeConfirmSubmit: string
    closing: string
    closeError: string
    closeSuccess: string
    reopenBetting: string
    reopening: string
    reopenError: string
    reopenSuccess: string
    prizeFirst: string
    topThree: string
    topOnlyNote: string
    bottomTwo: string
    prizeFirstHintExact: string
    bottomTwoHint: string
    prizeFirstInvalidExact: string
    bottomTwoInvalid: string
    frontThree: string
    bottomThree: string
    threeHint: string
    threeInvalid: string
    submitResult: string
    saveEdit: string
    submitting: string
    submitError: string
    enterSuccess: string
    editSuccess: string
    settleConfirmTitle: string
    settleConfirmDesc: string
    settleConfirmSubmit: string
    settling: string
    settleError: string
    voidConfirmTitle: string
    voidConfirmDesc: string
    voidConfirmSubmit: string
    voiding: string
    voidError: string
    resultSummary: (settled: number, won: number, lost: number) => string
    voidSummary: (reversed: number, totalReturned: string) => string
  }
  rounds: {
    title: string
    subtitle: string
    history: string
    roundNo: string
    game: string
    status: string
    openAt: string
    closeAt: string
    settleAt: string
    result: string
    noRounds: string
    filterGame: string
    filterStatus: string
    allGames: string
    allStatus: string
    sum: string
    highLow: string
    oddEven: string
    statusOpen: string
    statusClosed: string
    statusSettling: string
    statusSettled: string
    statusCancelled: string
    high: string
    low: string
    odd: string
    even: string
    unit: string
  }
  bets: {
    title: string
    subtitle: string
    history: string
    member: string
    game: string
    gameRound: string
    round: string
    type: string
    amount: string
    payout: string
    winAmount: string
    status: string
    time: string
    rollback: string
    rollbackConfirm: string
    rollbackSuccess: string
    rollbackError: string
    noBets: string
    filterStatus: string
    allStatus: string
    roundIdPlaceholder: string
    statusPending: string
    statusWon: string
    statusLost: string
    statusCancelled: string
    statusRefunded: string
    typeHigh: string
    typeLow: string
    typeOdd: string
    typeEven: string
  }
  kenoLimits: {
    title: string
    subtitle: string
    reload: string
    houseTitle: string
    houseHint: string
    colRange: string
    colStatus: string
    range: (min: string, max: string) => string
    statusActive: string
    statusOff: string
    add: string
    edit: string
    suspend: string
    reactivate: string
    empty: string
    minLabel: string
    maxLabel: string
    addTitle: string
    editTitle: string
    dialogHint: string
    invalidRange: string
    saveFailed: string
    savedLimit: string
    suspendedLimit: string
    reactivatedLimit: string
    assignTitle: string
    assignHint: string
    pickMaster: string
    noMasters: string
    partnerTitle: string
    partnerHint: string
    optionsEmpty: string
    nothingChosen: string
    membersSee: string
    blocked: string
    save: string
    clear: string
    saved: string
    cleared: string
    loadFailed: string
    agentCardTitle: string
    agentCardHint: string
  }
  partnerWallet: {
    title: string
    subtitle: string
    filterAttention: string
    filterFailed: string
    filterRetrying: string
    filterAll: string
    summaryFailed: (n: number) => string
    summaryRetrying: (n: number) => string
    colTime: string
    colKind: string
    colMember: string
    colAmount: string
    colRef: string
    colStatus: string
    colError: string
    kindCredit: string
    kindRollback: string
    kindSettle: string
    statusFailed: string
    statusRetrying: string
    statusDone: string
    statusCancelled: string
    statusPending: string
    attempts: (n: number) => string
    nextAttempt: string
    empty: string
    emptyFiltered: string
    retry: string
    resolve: string
    reload: string
    drawRef: string
    roundRef: string
    retryTitle: string
    retryDescription: (kind: string, amount: string, member: string) => string
    retryConfirm: string
    resolveTitle: string
    resolveDescription: (kind: string, amount: string, member: string) => string
    resolveNoteLabel: string
    resolveNoteHint: string
    resolveNext: string
    resolveConfirm: string
    doneRetry: string
    doneResolve: string
  }
  settlements: {
    title: string
    round: string
    totalBets: string
    totalBetAmount: string
    totalWinAmount: string
    totalLossAmount: string
    status: string
    processedAt: string
    errorMessage: string
    noSettlements: string
  }
  reports: {
    title: string
    winLoss: string
    from: string
    to: string
    agent: string
    allAgents: string
    generate: string
    generating: string
    totalBet: string
    betVolume: string
    totalWin: string
    payout: string
    netProfit: string
    agentNet: string
    masterNet: string
    adminNet: string
    agentShare: string
    masterShare: string
    memberCount: string
    roundCount: string
    noData: string
    noResults: string
    results: string
    totals: string
    exportCsv: string
    exporting: string
  }
  agents: {
    title: string
    createAgent: string
    code: string
    name: string
    commissionRate: string
    walletType: string
    status: string
    isActive: string
    apiKey: string
    rotateKey: string
    testConnection: string
    connectionOk: string
    connectionFail: string
    noAgents: string
    createError: string
    updateError: string
    internal: string
    seamless: string
    walletConfig: string
    walletUrl: string
    walletApiKey: string
    walletApiKeyHint: string
    walletHelpSeamless: string
    walletHelpInternal: string
    loginAccount: string
    loginAccountHint: string
    loginCreatedHint: (host: string) => string
    walletOptionalHint: string
    walletNotSet: string
    walletApiKeyKeep: string
    walletApiKeySet: (masked: string) => string
    resetPassword: string
    resetPasswordHint: string
    newPassword: string
    resetPasswordDone: string
    noLoginAccount: string
    resetPasswordError: string
    showPassword: string
    hidePassword: string
  }
  providers: {
    title: string
    subtitle: string
    code: string
    type: string
    symbols: string
    isActive: string
    toggle: string
    toggleError: string
    noProviders: string
    enable: string
    disable: string
    config: string
    typeCrypto: string
    typeForex: string
    typeGold: string
    typeStock: string
    typeOil: string
    typeIndex: string
    typeCustom: string
  }
  audit: {
    title: string
    actor: string
    action: string
    entity: string
    ipAddress: string
    detail: string
    noLogs: string
    viewDetail: string
  }
  docs: {
    title: string
    overview: string
    integrationFlow: string
    walletApi: string
    codeExamples: string
    authSection: string
    endpointsSection: string
    agentCode: string
    yourApiKey: string
    apiBase: string
    walletUrl: string
    overviewText: string
    authText: string
    method: string
    path: string
    description: string
    epBalance: string
    epDebit: string
    epCredit: string
    epRollback: string
    epSettle: string
    balanceQueryParam: string
    debitIdempotent: string
    rollbackDesc: string
    settleDesc: string
    flowStep0: string
    flowStep1: string
    flowStep2: string
    flowStep3: string
    flowStep4: string
    flowStep5: string
    flowStep6: string
    flowStep7: string
    flowStep8: string
    launchTitle: string
    launchExplain: string
    launchTokenFormat: string
    launchSecretLabel: string
    launchSecretNote: string
    launchRequestHeading: string
    launchResponseHeading: string
    launchGenerateHeading: string
    createMemberTitle: string
    createMemberExplain: string
    createMemberIdempotentNote: string
    createMemberRequestHeading: string
    createMemberResponseHeading: string
    kenoLaunchTitle: string
    kenoLaunchExplain: string
    kenoRefundNote: string
    drawLotteryTitle: string
    drawLotteryExplain: string
    drawLotteryMoneyNote: string
  }
  detail: {
    backToAgents: string
    agentNotFound: string
    loadFailed: string
    integrationDocs: string
    credentials: string
    agentCode: string
    apiKeyMasked: string
    newApiKeyNotice: string
    newApiKeyLabel: string
    rotateApiKey: string
    rotateApiKeyHint: string
    launchSecretMasked: string
    newLaunchSecretNotice: string
    newLaunchSecretLabel: string
    rotateLaunchSecret: string
    rotateLaunchSecretHint: string
    testing: string
    testConnection: string
    connected: string
    connectionFailed: string
    requestFailed: string
    settings: string
    displayName: string
    nameRequired: string
    commissionRate: string
    commissionRateHint: string
    commissionRateInvalid: string
    status: string
    activeLabel: string
    walletType: string
    agentWalletUrl: string
    walletUrlInvalid: string
    agentWalletApiKey: string
    agentWalletApiKeyHint: string
    showKey: string
    hideKey: string
    saveChanges: string
    saving: string
    saveSuccess: string
    saveFailed: string
    copyLabel: string
  }
}

export const translations: Record<Lang, Dict> = {
  th: {
    lang: {
      toggle: 'EN',
      toggleLabel: 'สลับภาษา',
      th: 'ไทย',
      en: 'English',
    },
    nav: {
      kenoLimits: 'วงเงิน KENO',
      partnerWallet: 'เงินค้างกับ partner',
      dashboard: 'แดชบอร์ด',
      rounds: 'รอบ',
      bets: 'การเดิมพัน',
      members: 'สมาชิก',
      games: 'เกม',
      settlements: 'การชำระ',
      reports: 'รายงาน',
      masters: 'มาสเตอร์',
      admins: 'จัดการแอดมิน',
      agents: 'เอเจนต์',
      providers: 'ผู้ให้ข้อมูล',
      auditLog: 'บันทึกกิจกรรม',
      ipWhitelist: 'IP ที่อนุญาต',
      management: 'จัดการ',
      profile: 'โปรไฟล์',
      numberLimits: 'ปิดรับเลข',
      myNumberLimits: 'ปิดรับเลข',
      numberLimitHistory: 'ประวัติการอั้นเลข',
      payoutRates: 'อัตราจ่าย',
      settings: 'การตั้งค่า',
      banners: 'แบนเนอร์หน้าแรก',
      keys: 'API Keys',
      docs: 'คู่มือ',
      draws: 'หวย',
      staging: 'ทดสอบเชื่อมต่อ',
      stagingTest: 'ยิงทดสอบ',
      stagingRuns: 'ประวัติผลทดสอบ',
      stagingReports: 'รายงานสายทดสอบ',
    },
    auth: {
      signIn: 'เข้าสู่ระบบ',
      signInSubtitle: 'Admin Console',
      username: 'ชื่อผู้ใช้',
      password: 'รหัสผ่าน',
      showPassword: 'แสดงรหัสผ่าน',
      hidePassword: 'ซ่อนรหัสผ่าน',
      submitting: 'กำลังเข้าสู่ระบบ...',
      adminOnly: 'เฉพาะผู้ดูแลระบบเท่านั้น',
      usernameRequired: 'กรุณาระบุชื่อผู้ใช้',
      passwordRequired: 'กรุณาระบุรหัสผ่าน',
      invalidCredentials: 'ชื่อผู้ใช้หรือรหัสผ่านไม่ถูกต้อง',
      networkError: 'ไม่สามารถเชื่อมต่อได้ กรุณาลองใหม่',
    },
    header: {
      history: 'ประวัติ',
      logout: 'ออกจากระบบ',
    },
    common: {
      loading: 'กำลังโหลด...',
      error: 'เกิดข้อผิดพลาด',
      retry: 'ลองใหม่',
      save: 'บันทึก',
      saving: 'กำลังบันทึก...',
      cancel: 'ยกเลิก',
      close: 'ปิด',
      create: 'สร้าง',
      creating: 'กำลังสร้าง...',
      edit: 'แก้ไข',
      delete: 'ลบ',
      actions: 'จัดการ',
      status: 'สถานะ',
      createdAt: 'วันที่สร้าง',
      updatedAt: 'อัปเดตล่าสุด',
      page: 'หน้า',
      of: 'จาก',
      prev: 'ก่อนหน้า',
      next: 'ถัดไป',
      total: 'รวม',
      items: 'รายการ',
      noData: 'ไม่มีข้อมูล',
      confirm: 'ยืนยัน',
      success: 'สำเร็จ',
      refresh: 'รีเฟรช',
      search: 'ค้นหา',
      filter: 'กรอง',
      all: 'ทั้งหมด',
      active: 'ใช้งาน',
      inactive: 'ไม่ใช้งาน',
      suspended: 'ระงับ',
      yes: 'ใช่',
      no: 'ไม่',
      username: 'ยูสเซอร์',
      password: 'รหัสผ่าน',
      displayName: 'ชื่อที่แสดง',
      optional: 'ไม่บังคับ',
      createdShort: 'สร้างเมื่อ',
      adminConsole: 'ระบบแอดมิน',
      masterConsole: 'ระบบมาสเตอร์',
      agentConsole: 'ระบบเอเจนต์',
      balance: 'ยอดเงิน',
    },
    dashboard: {
      title: 'แดชบอร์ด',
      totalMembers: 'สมาชิกทั้งหมด',
      activeMembers: 'สมาชิกที่ใช้งาน',
      betsToday: 'เดิมพันวันนี้',
      betVolume: 'ยอดเดิมพัน',
      payouts: 'ยอดจ่ายออก',
      houseEdge: 'กำไรบ้าน',
      openRounds: 'รอบที่เปิดอยู่',
      statsError: 'โหลดข้อมูลสถิติไม่สำเร็จ',
      maintenanceMode: 'โหมดปิดปรับปรุง',
      maintenanceActive: 'ปิดปรับปรุงอยู่ — สมาชิกเดิมพันไม่ได้',
    },
    members: {
      title: 'สมาชิก',
      createMember: 'เพิ่มสมาชิก',
      username: 'ชื่อผู้ใช้',
      displayName: 'ชื่อที่แสดง',
      role: 'บทบาท',
      status: 'สถานะ',
      wallet: 'กระเป๋าเงิน',
      loginAs: 'เข้าในนาม',
      balance: 'ยอดเงิน',
      credit: 'เติมเงิน',
      creditAmount: 'จำนวนเงิน',
      creditNote: 'หมายเหตุ',
      creditSuccess: 'เติมเงินสำเร็จ',
      impersonateError: 'เข้าในนามไม่สำเร็จ',
      chooseGameTitle: (u) => `เข้าในนาม "${u}"`,
      chooseGameDescription: 'เลือกระบบที่จะเปิดในแท็บใหม่',
      gameLottery: 'หวย',
      gameLotteryHint: 'หวยรัฐบาล หวยหุ้น และเกมราคา',
      gameKeno: 'KENO',
      gameKenoHint: 'KENO 4 โต๊ะ',
      walletError: 'โหลดกระเป๋าเงินไม่สำเร็จ',
      createError: 'สร้างสมาชิกไม่สำเร็จ',
      noMembers: 'ไม่มีสมาชิก',
      confirmImpersonate: (u) => `เข้าสู่ระบบในนาม "${u}"?`,
      createdViaApiNotice: 'สมาชิกถูกสร้างผ่าน API เท่านั้น (POST /api/external/create-member)',
      suspend: 'ระงับ',
      unsuspend: 'คืนสถานะ',
      suspendSuccess: 'เปลี่ยนสถานะสำเร็จ',
      suspendError: 'เปลี่ยนสถานะไม่สำเร็จ',
    },
    betReport: {
      title: 'รายงานเดิมพัน',
      member: 'สมาชิก',
      betCount: 'จำนวนบิล',
      betVolume: 'ยอดเดิมพัน',
      payout: 'ยอดจ่าย',
      net: 'ผลสุทธิ',
      viewBets: 'ดูบิล',
      memberBets: 'รายการเดิมพัน',
      noActivity: 'ไม่มีกิจกรรมในช่วงนี้',
      backToReport: 'กลับรายงาน',
      betType: 'ประเภท',
      result: 'ผล',
      game: 'เกม',
      date: 'วันที่',
    },
    numberCaps: {
      title: 'เพดานรายเลข',
      desc: 'ตั้งเพดานให้เลขใดเลขหนึ่งต่างจากค่าปกติของประเภท — สูงกว่าหรือต่ำกว่าก็ได้ เลขที่ไม่ได้ตั้งใช้ค่าปกติ ' +
        'ใช้กับงวดนี้และงวดถัดไปจนกว่าจะลบ',
      addNumbers: (digits) => `เลข (${digits} หลัก) — หลายเลขได้`,
      addChoice: 'ตัวเลือก',
      cap: 'เพดานรับต่อเลข',
      capPlaceholderHint: (def) => `ปกติ ${def}`,
      set: 'ตั้งเพดาน',
      worstCase: (amount) => `ถ้าเลขนี้ออก เว็บต้องจ่ายสูงสุด ${amount}`,
      colNumber: 'เลข',
      colCap: 'เพดานของเลขนี้',
      colVsDefault: 'เทียบกับปกติ',
      colTaken: 'รับแล้ว',
      colLoss: 'คาดเดายอดเสีย',
      lossHint: 'ถ้าเลขนี้ออก เว็บต้องจ่ายเท่าไหร่ = ยอดรับแล้ว × อัตราจ่ายของประเภท',
      lossIfFull: (amount) => `ถ้าเต็มเพดาน ${amount}`,
      sortHint: (column) => `กดเพื่อเรียงตาม${column} กดซ้ำเพื่อสลับมาก↔น้อย`,
      typeDefault: (def) => `ปกติ ${def}`,
      higher: (diff) => `สูงกว่า +${diff}`,
      lower: (diff) => `ต่ำกว่า −${diff}`,
      same: 'เท่าปกติ',
      overPool: (pool) => `เกินวงเงินรวมของประเภท (${pool}) — จะถูกจำกัดที่วงเงินรวม`,
      addToType: 'ตั้งเพิ่มประเภทนี้',
      editing: 'กำลังตั้งด้านบน',
      edit: 'แก้เพดาน',
      remove: 'ลบ (กลับไปใช้ค่าปกติ)',
      empty: 'ยังไม่มีเลขที่ตั้งเพดานเฉพาะ — ทุกเลขใช้ค่าปกติของประเภท',
      loadError: 'โหลดเพดานรายเลขไม่สำเร็จ',
      badDigits: (digits, bad) => `ประเภทนี้ต้องเป็นเลข ${digits} หลัก — แก้: ${bad}`,
      badChoice: (bad) => `ประเภทนี้รับเฉพาะ high / low / odd / even — แก้: ${bad}`,
      belowMin: (min) => `เพดานต้องไม่ต่ำกว่าเดิมพันขั้นต่ำ (${min}) — ถ้าไม่ต้องการรับเลขนี้ ให้ปิดรับเลข`,
      saved: (n) => `ตั้งเพดานแล้ว ${n} เลข`,
      otpTitle: 'ยืนยันการยกเพดาน',
      otpConfirm: 'ยืนยันยกเพดาน',
      otpSetDesc: (n, list) =>
        `จะยกเพดานรับ ${n} เลข (${list}) — การยกเพดานเพิ่มความเสี่ยงที่ต้องจ่าย จึงต้องใช้รหัสยืนยัน การลดเพดานไม่ต้องใช้รหัส`,
      otpRemoveDesc: (number, now, def) =>
        `จะลบเพดานของเลข ${number} (ตอนนี้ ${now}) — เลขจะกลับไปรับตามค่าปกติ ${def} ซึ่งสูงกว่า จึงต้องใช้รหัสยืนยัน`,
      otpLimitsDesc: (type, list) =>
        `จะยกเพดานรับของ ${type} (${list}) — การยกเพดานเพิ่มความเสี่ยงที่ต้องจ่าย จึงต้องใช้รหัสยืนยัน การลดเพดานไม่ต้องใช้รหัส`,
    },
    agentNumberRules: {
      title: 'ปิดรับเลข',
      desc: 'ปิดรับเลข ตั้งเพดานรับ และลดอัตราจ่าย เฉพาะลูกค้าของคุณ — ไม่กระทบเอเย่นต์อื่น',
      carriesOver: 'กฎที่ตั้งไว้ใช้กับงวดนี้และงวดถัดไปเรื่อย ๆ — ไม่หายเองเมื่อจบงวด จนกว่าจะกดล้าง',
      clearAll: 'ล้างทั้งหมด',
      clearTitle: 'ล้างเลขอั้นทั้งหมด?',
      clearDesc: (count, lottery) =>
        `จะลบกฎทั้ง ${count} รายการของ ${lottery} (ทุกประเภทเดิมพัน รวมกฎทั้งประเภท) ` +
        'ลูกค้าจะกลับไปแทงได้ตามเงื่อนไขของเว็บทันที และกู้คืนไม่ได้',
      clearConfirm: 'ยืนยัน ล้างทั้งหมด',
      closeNumbers: 'ปิดรับเลข',
      setLimit: 'ตั้งเงื่อนไข',
      wholeType: 'ทั้งประเภท',
      housePayout: 'อัตราจ่ายของเว็บ',
      typePayout: 'อัตราจ่ายทั้งประเภท',
      myPayout: 'อัตราจ่ายของฉัน',
      myCap: 'เพดานของฉัน',
      effCap: 'เพดานที่มีผล',
      effPayout: 'อัตราจ่ายที่มีผล',
      fromHouse: 'ตามเว็บ',
      fromType: 'ตามทั้งประเภท',
      myNumbers: 'เลขที่ตั้งไว้',
      addTitle: 'เพิ่มเลข',
      manageType: 'ตั้งค่าประเภทนี้',
      editingAbove: 'กำลังตั้งค่าด้านบน',
      typeRuleOnly: 'ตั้งไว้ทั้งประเภท — ยังไม่มีเลขรายตัว',
      mineVsHouse: 'ยอดของฉัน / ทั้งเว็บ',
      blocked: 'ปิดรับ',
      block: 'ปิดรับเลขนี้',
      unblock: 'เปิดรับเลขนี้',
      blockType: 'ปิดรับทั้งประเภท',
      unblockType: 'เปิดรับทั้งประเภท',
      addNumbers: (digits) => `เลข (${digits} หลัก) — หลายเลขได้`,
      addChoice: 'ตัวเลือก',
      addHint: 'ใส่เฉพาะเลข แล้วกด ปิดรับเลข · หรือใส่เพดาน/อัตราจ่ายด้วย แล้วกด ตั้งเงื่อนไข (ยังแทงได้แต่ถูกจำกัด) · ช่องที่ว่างอยู่จะใช้ค่าสีจางที่เห็น',
      empty: 'ยังไม่มีเลขที่ตั้งกฎไว้ และยังไม่มีลูกค้าแทงประเภทนี้ในงวดนี้',
      clamped: (asked, actual) => `ตั้งไว้ ${asked} · มีผลจริง ${actual} (เว็บลดเพดานลงแล้ว)`,
      badDigits: (digits, bad) => `ประเภทนี้ต้องเป็นเลข ${digits} หลัก — แก้: ${bad}`,
      badChoice: (bad) => `ประเภทนี้รับเฉพาะ high / low / odd / even — แก้: ${bad}`,
    },
    agentSettings: {
      title: 'การตั้งค่า',
      maintenanceMode: 'โหมดปิดปรับปรุง',
      maintenanceDesc: 'เมื่อเปิด สมาชิกของคุณจะไม่สามารถวางเดิมพันได้ (ไม่กระทบ agent อื่น)',
      maintenanceOn: 'เปิดปิดปรับปรุง',
      maintenanceOff: 'ปิดปิดปรับปรุง',
      toggleSuccess: 'เปลี่ยนสถานะสำเร็จ',
      toggleError: 'เปลี่ยนสถานะไม่สำเร็จ',
      walletApiKeyTitle: 'Wallet API Key',
      walletApiKeyDesc: 'คีย์ที่ server ฝั่งคุณใช้ตรวจสอบคำขอจาก Lotmatic (X-Api-Key) — สร้างขึ้นเอง ไม่ใช่ค่าที่ Lotmatic ออกให้',
      walletApiKeyLabel: 'Wallet API Key',
      walletApiKeyPlaceholder: 'อย่างน้อย 16 ตัวอักษร',
      walletApiKeyCurrentNotSet: 'ยังไม่ได้ตั้งค่า',
      walletApiKeySave: 'บันทึก',
      walletApiKeySaving: 'กำลังบันทึก...',
      walletApiKeySaveSuccess: 'บันทึก Wallet API Key สำเร็จ',
      walletApiKeySaveError: 'บันทึกไม่สำเร็จ',
    },
    agentKeys: {
      title: 'API Keys',
      nexbitApiKey: 'Lotmatic API Key',
      nexbitApiKeyDesc: 'ส่งใน X-Partner-Key header เมื่อเรียก /api/external/*',
      launchSecret: 'Launch Secret',
      launchSecretDesc: 'ใช้เซ็น launchToken (HMAC-SHA256) — สร้างฝั่ง server เท่านั้น',
      rotate: 'หมุน Key ใหม่',
      rotating: 'กำลังหมุน...',
      rotateSuccess: 'หมุน Key สำเร็จ',
      rotateWarning: 'Key เดิมจะใช้ไม่ได้ทันที — ต้องอัปเดตฝั่ง server ของคุณด้วย',
      newKeyNotice: 'Key ใหม่ — คัดลอกตอนนี้เท่านั้น:',
      copyNow: 'คัดลอก',
    },
    agentPassword: {
      title: 'เปลี่ยนรหัสผ่าน',
      current: 'รหัสผ่านปัจจุบัน',
      newPassword: 'รหัสผ่านใหม่',
      confirm: 'ยืนยันรหัสผ่านใหม่',
      mismatch: 'รหัสผ่านไม่ตรงกัน',
      tooShort: 'รหัสผ่านต้องมีอย่างน้อย 8 ตัวอักษร',
      success: 'เปลี่ยนรหัสผ่านสำเร็จ',
      wrong: 'รหัสผ่านปัจจุบันไม่ถูกต้อง',
      change: 'เปลี่ยนรหัสผ่าน',
      changing: 'กำลังเปลี่ยน...',
    },
    games: {
      title: 'เกม',
      subtitle: 'ตั้งค่าเกมและอัตราจ่าย',
      config: 'ตั้งค่าเกม',
      unit: 'เกม',
      gameCode: 'รหัสเกม',
      minBet: 'แทงต่ำสุด',
      maxBet: 'แทงสูงสุด',
      duration: 'ระยะเวลา',
      betClose: 'ปิดรับก่อน',
      cooldown: 'คูลดาวน์',
      payoutHigh: 'อัตราจ่าย สูง',
      payoutLow: 'อัตราจ่าย ต่ำ',
      payoutOdd: 'อัตราจ่าย คี่',
      payoutEven: 'อัตราจ่าย คู่',
      highThreshold: 'เกณฑ์สูง',
      isActive: 'ใช้งาน',
      saveSuccess: 'บันทึกการตั้งค่าสำเร็จ',
      saveError: 'บันทึกไม่สำเร็จ',
      noGames: 'ไม่มีเกม',
      seconds: 'วินาที',
      secShort: 'วิ',
      tradingSchedule: 'ตารางเวลาซื้อขาย',
      roundSec: 'รอบ (วิ)',
      closeBeforeSec: 'ปิดก่อน (วิ)',
      highX: 'สูง ×',
      lowX: 'ต่ำ ×',
      oddX: 'คี่ ×',
      evenX: 'คู่ ×',
      metaRound: 'รอบ',
      metaClose: 'ปิดก่อน',
      metaPayout: 'จ่าย',
    },
    draws: {
      title: 'หวย',
      lotteries: 'หวย',
      lotteryList: 'รายการหวย',
      selectLottery: 'เลือกหวยเพื่อดูงวด',
      category: 'ประเภท',
      categoryDraw: 'หวยงวด',
      categoryStock: 'หวยหุ้น',
      recentDraws: 'งวดล่าสุด',
      drawNumber: 'งวดที่',
      drawDate: 'วันที่ออก',
      status: 'สถานะ',
      closeAt: 'ปิดรับ',
      noLotteries: 'ไม่มีหวย',
      noDraws: 'ไม่มีงวด',
      statusScheduled: 'รอเปิด',
      statusOpen: 'เปิดรับแทง',
      statusClosed: 'ปิดรับแล้ว',
      statusResulted: 'ออกผลแล้ว',
      statusSettled: 'ชำระแล้ว',
      statusCancelled: 'ยกเลิกแล้ว',
      statusSkipped: 'งดออกผล',
      skipDraw: 'งดออกผล',
      skipTitle: 'งดออกผลงวดนี้',
      skipDesc: 'ใช้เมื่องวดนี้ไม่มีการออกผล (เช่น ตลาดหยุด) — บิลทุกใบคืนเงินให้ผู้เล่นทันที งวดจะแสดงว่า "งดออกผล" ให้สมาชิกเห็น และระบบจะไม่เปิดงวดรอบนี้ซ้ำอีก',
      skipConfirm: 'ยืนยันงดออกผลและคืนเงิน',
      skipDone: (n, amount) => `งดออกผลแล้ว — คืนเงิน ${n} บิล รวม ฿${amount}`,
      skipError: 'งดออกผลไม่สำเร็จ',
      cancelDraw: 'ยกเลิกงวด',
      cancelTitle: 'ยกเลิกงวดและคืนเงิน',
      cancelDesc: 'ใช้เมื่องวดนี้จะไม่มีผลออก — บิลทุกใบจะถูกยกเลิกและคืนเงินเข้ากระเป๋าผู้เล่นทันที ย้อนกลับไม่ได้',
      cancelReason: 'เหตุผล',
      cancelReasonHint: 'บันทึกไว้ในประวัติกิจกรรม เช่น ตลาดไม่เปิดทำการ',
      cancelConfirm: 'ยืนยันยกเลิกและคืนเงิน',
      cancelDone: (n, amount) => `ยกเลิกงวดแล้ว — คืนเงิน ${n} บิล รวม ฿${amount}`,
      cancelError: 'ยกเลิกงวดไม่สำเร็จ',
      bettingOpen: 'ยังเปิดรับแทงอยู่',
      readyForResult: 'ปิดรับแล้ว — พร้อมป้อนผล',
      result: 'ผล',
      enterResult: 'ป้อนผล',
      editResult: 'แก้ผล',
      settle: 'จ่ายเงิน',
      voidAction: 'ยกเลิก',
      payout: 'อัตราจ่าย',
      payoutTitle: 'อัตราจ่ายและวงเงิน',
      payoutDesc:
        'แก้อัตราจ่ายและวงเงินแทงของหวยนี้ บิลที่แทงไปแล้วใช้อัตราเดิมเสมอ การเปลี่ยนมีผลกับบิลที่แทงหลังบันทึกเท่านั้น',
      payoutSubmit: 'ยืนยันและบันทึก',
      payoutError: 'บันทึกอัตราจ่ายไม่สำเร็จ',
      payoutSaved: 'บันทึกอัตราจ่ายแล้ว',
      payoutOpenWarning:
        'ขณะนี้มีงวดเปิดรับแทงอยู่ — ผู้เล่นจะเห็นอัตราใหม่ทันทีที่บันทึก บิลที่ส่งไปแล้วยังจ่ายตามอัตราเดิม',
      payoutBetType: 'ประเภทแทง',
      payoutEnabled: 'เปิดใช้',
      payoutRate: 'อัตราจ่าย',
      payoutMin: 'ขั้นต่ำ',
      payoutMax: 'สูงสุด/บิล',
      payoutPerNumber: 'สูงสุด/เลข',
      payoutPerType: 'รับรวม/ประเภท',
      exposure: 'ยอดรับ',
      exposureTitle: 'ยอดรับของงวด',
      exposureDesc: 'ยอดเงินที่รับมาแล้วในงวดนี้ เทียบกับวงเงินที่ตั้งไว้ อัปเดตอัตโนมัติทุก 5 วินาที',
      exposureEmpty: 'ยังไม่มีบิลในงวดนี้',
      exposureTaken: 'รับแล้ว',
      exposureBets: 'บิล',
      exposureFull: 'เต็มแล้ว',
      exposureTopNumbers: 'เลขที่รับมากที่สุด',
      exposureAuto: 'อัปเดตอัตโนมัติ',
      blockTitle: 'อั้นเลข',
      blockPageDesc: 'ตั้งเพดานรับของแต่ละประเภท ดูยอดรับรายเลข และอั้นเลขที่รับมากเกินไป',
      limitsTitle: 'เพดานรับ',
      limitsNoConfig: 'ประเภทนี้ยังไม่ได้ตั้งค่าในหวยนี้',
      limitsMaxBetHint: 'ต่อ 1 บิล',
      limitsPerNumberHint: 'รวมทั้งงวด ต่อ 1 เลข',
      limitsPerTypeHint: 'รวมทั้งงวด ทุกเลขของประเภทนี้',
      limitsSaved: 'บันทึกแล้ว',
      limitsNoBets: 'ยังไม่มีการเดิมพันประเภทนี้ในงวดนี้',
      limitsNumber: 'เลข',
      limitsFill: 'เต็มไปเท่าไหร่',
      limitsClosed: 'อั้นแล้ว',
      limitsLiability: 'คาดเดายอดเสีย',
      limitsWorstCase: 'เสียมากสุดถ้าเลขหนักออก',
      limitsWorstNet: 'ขาดทุนสุทธิ',
      limitsWorstHint: '(หักยอดรับทั้งประเภทแล้ว — ออกได้เลขเดียว จึงคิดจากเลขที่หนักที่สุด)',
      blockNoLiveDraw: 'ยังไม่มีงวดที่เปิดรับแทง — อั้นได้เฉพาะแบบทุกงวด',
      blockHint: 'เลขที่อั้นจะแทงไม่ได้ทันที บิลที่รับไปแล้วยังจ่ายตามปกติ',
      blockAdd: 'อั้นเลข',
      blockEmpty: 'ยังไม่มีเลขที่อั้นในงวดนี้',
      blockAllDraws: 'อั้นทุกงวด',
      blockAllBadge: 'ทุกงวด',
      blockRemove: 'ปลดอั้น',
      payoutCeiling: (max) => `ไม่เกิน ×${max}`,
      payoutInvalid: (types) => `ค่าไม่ถูกต้อง: ${types}`,
      payoutChanges: 'รายการที่จะเปลี่ยน',
      payoutPassword: 'รหัสผ่านของคุณ',
      payoutPasswordHint: 'ยืนยันตัวตนก่อนบันทึก — ป้อนผิดหลายครั้งจะถูกระงับชั่วคราว',
      betTypeLabel: {
        '3top': '3 ตัวบน',
        '3tote': '3 ตัวโต๊ด',
        '3front': '3 ตัวหน้า',
        '3bottom': '3 ตัวล่าง',
        '2top': '2 ตัวบน',
        '2bottom': '2 ตัวล่าง',
        run_top: 'วิ่งบน',
        run_bottom: 'วิ่งล่าง',
        odd_even_top: 'คู่/คี่ บน',
        high_low_top: 'สูง/ต่ำ บน',
        odd_even_bottom: 'คู่/คี่ ล่าง',
        high_low_bottom: 'สูง/ต่ำ ล่าง',
      },
      settings: 'ตั้งค่าหวย',
      settingsTitle: 'ตั้งค่าหวย',
      settingsDesc:
        'ตั้งตารางออกผลไว้ล่วงหน้า เวลาของงวดถัดไปจะคำนวณจากตารางนี้ให้อัตโนมัติ ไม่ต้องกรอกเองทุกงวด',
      settingsError: 'บันทึกการตั้งค่าไม่สำเร็จ',
      settingsSaved: 'บันทึกการตั้งค่าหวยแล้ว',
      rules: 'กติกา',
      rulesTitle: (name: string) => `กติกา — ${name}`,
      rulesHint:
        'เขียนวิธีออกผลและเงื่อนไขของหวยนี้ สมาชิกจะเห็นข้อความนี้คู่กับตารางอัตราจ่าย (ตารางอัตราจ่ายสร้างจากค่าที่ตั้งไว้ ไม่ต้องพิมพ์เอง)',
      rulesTabEdit: 'แก้ไข',
      rulesTabPreview: 'ตัวอย่าง',
      rulesLoadError: 'โหลดกติกาไม่สำเร็จ',
      rulesSaveError: 'บันทึกกติกาไม่สำเร็จ',
      rulesSaved: 'บันทึกกติกาแล้ว',
      scheduleType: 'รอบการออกผล',
      scheduleTypeLabel: {
        daily: 'ทุกวัน',
        weekly: 'รายสัปดาห์',
        monthly: 'รายเดือน',
        manual: 'กำหนดเอง',
      },
      scheduleDays: 'วันที่ออก (เลือกได้หลายวัน)',
      scheduleDates: 'วันที่ของเดือน (เลือกได้หลายวัน)',
      scheduleDatesHint: 'เดือนที่ไม่มีวันที่นั้นจะข้ามไปเดือนถัดไป เช่น เลือก 31 เดือนกุมภาพันธ์จะถูกข้าม',
      scheduleTime: 'เวลาออกผล (ICT)',
      scheduleTimeHint: 'เวลาไทย ไม่มี DST',
      addLottery: 'เพิ่มหวย',
      addLotteryDone: (name) => `เพิ่มหวย ${name} แล้ว — ยังปิดอยู่ เปิดใช้งานได้จากรายการหวย`,
      scheduleRounds: (n) => `${n} รอบ`,
      roundAdd: 'เพิ่มรอบ',
      roundLabelPlaceholder: 'ชื่อรอบ เช่น รอบเช้า',
      roundTimeOf: (n) => `เวลาออกผล รอบที่ ${n}`,
      roundLabelOf: (n) => `ชื่อรอบที่ ${n}`,
      roundRemoveOf: (n) => `ลบรอบที่ ${n}`,
      roundDuplicate: 'เวลาซ้ำกับรอบอื่น — แต่ละรอบต้องคนละเวลา',
      roundLabelRequired: 'หวยที่ออกหลายรอบ ต้องตั้งชื่อรอบให้ครบทุกรอบ',
      roundTimeInvalid: 'เวลาไม่ถูกต้อง',
      roundOpenOf: (n) => `เวลาเปิดรับแทง รอบที่ ${n}`,
      roundOpenLabel: 'เปิดรับแทง',
      roundOpenInherit: 'ใช้เวลาเปิดของหวยด้านล่าง',
      roundOpenInvalid: 'เวลาเปิดรับแทงไม่ถูกต้อง',
      roundOpenAfterClose: 'เวลาเปิดรับแทงต้องมาก่อนเวลาปิดรับ ไม่งั้นงวดนี้จะแทงไม่ได้เลย',
      roundOpenComputed: (duration) => `≈ ${duration} ก่อนออกผลของรอบนี้`,
      roundOpenModeInherit: 'ใช้ค่าของหวย',
      roundOpenModeTime: 'เวลา',
      roundOpenModeAfterPrev: 'หลังรอบก่อนหน้าออกผล',
      roundOpenAfterPrevOf: (n) => `เปิดหลังรอบก่อนหน้าออกผล รอบที่ ${n}`,
      roundOpenAfterPrevHint: 'นับจากเวลาออกผลของรอบก่อนหน้า (ชม.:นาที)',
      roundOpenAfterPrevComputed: (duration) => `≈ หลังรอบก่อนหน้าออกผล ${duration}`,
      roundOpenAfterPrevNow: 'เปิดทันทีที่รอบก่อนหน้าออกผล',
      roundOpenAfterPrevTooLate: 'ช้าเกินไป — จะเลยเวลาปิดรับของรอบนี้ ทำให้แทงไม่ได้เลย',
      createAfterEnding: 'เปิดงวดถัดไปเลยไหม',
      createAfterCancelNote: 'ยกเลิกงวดแล้ว — งวดนี้จบไปโดยไม่มีการจ่ายเงิน จึงยังไม่มีงวดถัดไป',
      createAfterSkipNote: 'งดออกผลแล้ว — งวดนี้จบไปโดยไม่มีการจ่ายเงิน จึงยังไม่มีงวดถัดไป',
      roundsMax: (n) => `สูงสุด ${n} รอบต่อวัน`,
      roundsPreview: (list) => `จะบันทึกเป็น: ${list}`,
      roundsChangeNote: 'การแก้รอบมีผลกับงวดที่เปิดหลังจากนี้ งวดที่เปิดไปแล้วไม่ถูกแก้',
      round: 'รอบ',
      scheduleDaily: 'ทุกวัน',
      scheduleManual: 'กำหนดเอง — ไม่มีตารางออกผล',
      scheduleManualHint: 'เมื่อเลือกกำหนดเอง ต้องกรอกเวลาทุกครั้งที่เปิดงวดใหม่',
      scheduleMonthlyOn: (dates) => `วันที่ ${dates} ของทุกเดือน`,
      weekdayShort: ['อา', 'จ', 'อ', 'พ', 'พฤ', 'ศ', 'ส'],
      closeBefore: 'ปิดรับก่อนออกผล (นาที)',
      closeBeforeHint: 'ตัวเลข 0–1440 นาที',
      openBefore: 'เปิดให้แทงก่อนออกผล',
      openBeforeOff: 'ทันทีที่สร้างงวด',
      openBeforeDays: 'ตามจำนวนวัน',
      openBeforeTime: 'เวลาเปิด',
      openBeforeOffHint: 'งวดใหม่รับแทงได้ทันทีที่เปิด — ค่าเริ่มต้นของหวยทั่วไป',
      openBeforeDaysHint: 'ใส่ตัวเลข 0–60 วัน เช่น หวยไทยใส่ 10',
      openBeforePlaceholder: '10',
      // "ทุกครั้งที่มีงวด" ไม่ใช่ "ทุกวัน" — หวยที่ออกผลไม่กี่วันต่อสัปดาห์ (เช่น หวยมาเลย์
      // อา/พ/ส) เปิดแทงตามวันที่ออกผลจริงเท่านั้น ไม่ใช่ทุกวันของสัปดาห์.
      openBeforeTimeHint: 'ทุกครั้งที่มีงวด จะเริ่มรับแทงเวลานี้ ก่อนถึงรอบออกผล',
      openBeforeTimeComputed: (duration) => `≈ ${duration} ก่อนออกผล`,
      openBeforeTimeRoundNote: (time) =>
        `คำนวณจากเวลารอบแรก (${time}) และใช้กับรอบที่ไม่ได้ตั้งเวลาเปิดของตัวเองไว้`,
      openAfterPrevious: 'เปิดเร็วขึ้นหลังงวดก่อนหน้า',
      // สำหรับหวยที่ปิดรับแทงนานผิดปกติ (เช่นตลาดที่รายงานผลช้า) จนเวลาเปิดปกติชนกับ
      // เวลาปิด — ดูตัวอย่างหวยหุ้นดาวโจนส์ที่ปิดรับ 7 ชม. ก่อนออกผล.
      openAfterPreviousHint:
        'ใช้เมื่อรอบก่อนหน้าห่างไม่เกิน 1.5 วัน ไม่งั้นใช้ค่า "เวลาเปิด" ด้านบนแทน — สำหรับหวยที่ปิดรับแทงนานผิดปกติ เช่น หวยหุ้นดาวโจนส์',
      openAfterPreviousComputed: (duration) => `≈ ${duration} หลังงวดก่อนหน้าประกาศผล`,
      closeBeforeShort: (mins) => `ปิดรับก่อน ${mins} นาที`,
      createDraw: 'เปิดงวดใหม่',
      createTitle: 'เปิดงวดใหม่',
      createDesc:
        'เวลาด้านล่างคำนวณจากตารางของหวยนี้ ตรวจสอบแล้วกดยืนยันเพื่อเปิดงวด',
      createDescManual:
        'กรอกเวลาของงวดนี้เอง (ใช้กับหวยที่ไม่ได้ตั้งตาราง หรือรอบพิเศษ)',
      createEditTimes: 'แก้เวลาเอง',
      createSubmit: 'เปิดงวด',
      creating: 'กำลังเปิดงวด...',
      createError: 'เปิดงวดใหม่ไม่สำเร็จ',
      createSuccess: 'เปิดงวดใหม่แล้ว',
      createOrderInvalid: 'ต้องเรียงเวลา: เปิดรับ < ปิดรับ ≤ ออกผล',
      createBlocked: (n, closeAt) =>
        `เปิดงวดใหม่ไม่ได้ — งวดที่ ${n} ยังเปิดรับแทงอยู่ (ปิดรับ ${closeAt}) รอปิดรับก่อน`,
      createStartsOpen: 'งวดนี้จะเปิดรับแทงทันทีที่กดยืนยัน',
      createStartsScheduled: 'งวดนี้จะขึ้นสถานะ "ยังไม่เปิด" จนถึงเวลาเปิดรับ แล้วเปิดเองอัตโนมัติ',
      openAt: 'เปิดรับแทง',
      openAtHint: 'เวลาที่ลูกค้าเริ่มแทงได้',
      closeAtHint: 'เวลาที่หยุดรับบิล',
      drawAt: 'เวลาออกผล',
      drawAtHint: 'เวลาที่ประกาศผลงวดนี้',
      clockNoDraw: 'ยังไม่เปิดงวด',
      clockAwaitingResult: 'รอออกผล',
      clockAwaitingPayout: 'รอจ่ายเงิน',
      unitDay: 'วัน',
      unitHour: 'ชม.',
      unitMin: 'น.',
      marketOpen: 'ตลาดเปิด',
      marketClosed: 'ตลาดปิด',
      marketToggleError: 'สลับสถานะตลาดไม่สำเร็จ',
      closeBetting: 'ปิดรับแทง',
      closeConfirmTitle: 'ยืนยันปิดรับแทง',
      closeConfirmDesc:
        'ปิดรับแทงงวดนี้ทันที โดยไม่รอเวลาปิดตามกำหนด บิลที่ส่งเข้ามาแล้วยังอยู่ครบ แต่คนที่กำลังกดโพยอยู่จะส่งไม่ได้ — กดปิดผิดแล้วยังไม่ได้ป้อนผล เปิดรับแทงคืนได้',
      closeConfirmSubmit: 'ยืนยันปิดรับ',
      closing: 'กำลังปิดรับ...',
      closeError: 'ปิดรับแทงไม่สำเร็จ',
      closeSuccess: 'ปิดรับแทงแล้ว — ป้อนผลได้เลย',
      reopenBetting: 'เปิดรับแทงคืน',
      reopening: 'กำลังเปิดรับแทงคืน...',
      reopenError: 'เปิดรับแทงคืนไม่สำเร็จ',
      reopenSuccess: 'เปิดรับแทงคืนแล้ว',
      prizeFirst: 'รางวัลที่ 1',
      topThree: '3 ตัวบน',
      topOnlyNote: 'หวยนี้ออกผลเฉพาะ 3 ตัวบน — ไม่มี 2 ตัวล่าง (ปิดแทงล่างไว้แล้ว)',
      bottomTwo: '2 ตัวล่าง',
      prizeFirstHintExact: 'ตัวเลข {n} หลัก',
      bottomTwoHint: 'ตัวเลข 2 หลัก',
      prizeFirstInvalidExact: 'ต้องเป็นตัวเลข {n} หลักพอดี',
      bottomTwoInvalid: '2 ตัวล่าง ต้องเป็นตัวเลข 2 หลักพอดี',
      frontThree: 'เลขหน้า 3 ตัว',
      bottomThree: 'เลขท้าย 3 ตัว',
      threeHint: 'ออก 2 ชุด ชุดละ 3 หลัก',
      threeInvalid: 'ต้องเป็นตัวเลข 3 หลักพอดี ทั้ง 2 ชุด',
      submitResult: 'ป้อนผล',
      saveEdit: 'บันทึกการแก้ไข',
      submitting: 'กำลังบันทึก...',
      submitError: 'บันทึกผลไม่สำเร็จ',
      enterSuccess: 'ป้อนผลแล้ว — ตรวจสอบผลแล้วกดจ่ายเงิน',
      editSuccess: 'แก้ผลเรียบร้อย',
      settleConfirmTitle: 'ยืนยันการจ่ายเงิน',
      settleConfirmDesc:
        'ระบบจะจ่ายเงินรางวัลให้ผู้ชนะทุกบิลในงวดนี้ตามผลด้านล่างทันที ตรวจสอบผลให้ถูกต้องก่อนยืนยัน (หากผิดพลาดยังกด "ยกเลิก" เพื่อเรียกเงินคืนได้ภายหลัง)',
      settleConfirmSubmit: 'ยืนยันจ่ายเงิน',
      settling: 'กำลังจ่ายเงิน...',
      settleError: 'จ่ายเงินไม่สำเร็จ',
      voidConfirmTitle: 'ยืนยันการยกเลิก',
      voidConfirmDesc:
        'จะเรียกเงินรางวัลคืนจากผู้ชนะทั้งหมด แล้วนำงวดกลับไปสถานะ "ออกผลแล้ว" เพื่อให้แก้ผลและจ่ายเงินใหม่ได้',
      voidConfirmSubmit: 'ยืนยันยกเลิก',
      voiding: 'กำลังยกเลิก...',
      voidError: 'ยกเลิกไม่สำเร็จ',
      resultSummary: (settled, won, lost) =>
        `ชำระแล้ว ${settled} บิล — ชนะ ${won} · แพ้ ${lost}`,
      voidSummary: (reversed, totalReturned) =>
        `เรียกเงินคืนแล้ว ${reversed} บิล — รวม ${totalReturned}`,
    },
    rounds: {
      title: 'รอบ',
      subtitle: 'ประวัติรอบเกมทั้งหมด',
      history: 'ประวัติรอบ',
      roundNo: 'รอบที่',
      game: 'เกม',
      status: 'สถานะ',
      openAt: 'เปิด',
      closeAt: 'ปิด',
      settleAt: 'ชำระเมื่อ',
      result: 'ผล',
      noRounds: 'ไม่พบรอบที่ตรงกับเงื่อนไข',
      filterGame: 'กรองตามเกม',
      filterStatus: 'กรองตามสถานะ',
      allGames: 'ทุกเกม',
      allStatus: 'ทุกสถานะ',
      sum: 'ผลรวม',
      highLow: 'สูง/ต่ำ',
      oddEven: 'คี่/คู่',
      statusOpen: 'เปิด',
      statusClosed: 'ปิด',
      statusSettling: 'กำลังจ่าย',
      statusSettled: 'จ่ายแล้ว',
      statusCancelled: 'ยกเลิก',
      high: 'สูง',
      low: 'ต่ำ',
      odd: 'คี่',
      even: 'คู่',
      unit: 'รอบ',
    },
    bets: {
      title: 'การเดิมพัน',
      subtitle: 'ประวัติการเดิมพันของสมาชิกทั้งหมด',
      history: 'ประวัติการเดิมพัน',
      member: 'สมาชิก',
      game: 'เกม',
      gameRound: 'เกม / รอบ',
      round: 'รอบ',
      type: 'ประเภท',
      amount: 'ยอดเดิมพัน',
      payout: 'อัตราจ่าย',
      winAmount: 'รางวัล',
      status: 'สถานะ',
      time: 'เวลา',
      rollback: 'คืนโพย',
      rollbackConfirm: 'ยืนยันคืนโพย',
      rollbackSuccess: 'คืนโพยสำเร็จ',
      rollbackError: 'คืนโพยไม่สำเร็จ',
      noBets: 'ไม่พบรายการเดิมพัน',
      filterStatus: 'กรองตามสถานะ',
      allStatus: 'ทุกสถานะ',
      roundIdPlaceholder: 'ค้นหาด้วยรหัสรอบ...',
      statusPending: 'รอผล',
      statusWon: 'ชนะ',
      statusLost: 'ไม่ถูก',
      statusCancelled: 'ยกเลิก',
      statusRefunded: 'คืนเงิน',
      typeHigh: 'สูง',
      typeLow: 'ต่ำ',
      typeOdd: 'คี่',
      typeEven: 'คู่',
    },
    kenoLimits: {
      title: 'วงเงิน KENO',
      subtitle: 'แถวใน “CHOOSE YOUR BET LIMIT” ของเกม KENO — เพิ่ม แก้ หรือระงับได้ แล้วกำหนดว่าแต่ละ master / เอเจนต์ ให้สมาชิกเลือกแถวไหนได้บ้าง',
      reload: 'โหลดใหม่',
      houseTitle: 'แถววงเงินทั้งหมด',
      houseHint: 'แถวที่ระงับจะไม่ขึ้นให้ใครเลือก และบิลที่ส่งมาด้วยแถวนั้นจะถูกปฏิเสธ — บิลที่แทงไปแล้วไม่ได้รับผลกระทบ',
      colRange: 'ช่วงแทงต่อบิล (ต่ำสุด – สูงสุด)',
      colStatus: 'สถานะ',
      range: (min, max) => `${min} – ${max}`,
      statusActive: 'ใช้งาน',
      statusOff: 'ระงับ',
      add: 'เพิ่มแถว',
      edit: 'แก้ไข',
      suspend: 'ระงับใช้งาน',
      reactivate: 'เปิดใช้งาน',
      empty: 'ยังไม่มีแถววงเงิน — สมาชิกจะเข้าเล่น KENO ไม่ได้จนกว่าจะเพิ่ม',
      minLabel: 'ต่ำสุดต่อบิล (บาท)',
      maxLabel: 'สูงสุดต่อบิล (บาท)',
      addTitle: 'เพิ่มแถววงเงิน',
      editTitle: 'แก้ไขแถววงเงิน',
      dialogHint: 'เป็นจำนวนเต็ม และไม่ซ้ำกับแถวที่มีอยู่ การแก้ช่วงมีผลกับบิลถัดไปทันที',
      invalidRange: 'ต่ำสุดต้องเป็นเลขจำนวนเต็มตั้งแต่ 1 และสูงสุดต้องไม่น้อยกว่าต่ำสุด',
      saveFailed: 'บันทึกไม่สำเร็จ',
      savedLimit: 'บันทึกแถววงเงินแล้ว',
      suspendedLimit: 'ระงับแถวนี้แล้ว',
      reactivatedLimit: 'เปิดใช้งานแถวนี้แล้ว',
      assignTitle: 'กำหนดให้ Master',
      assignHint: 'เลือก master แล้วติ๊กแถวที่สายนี้ใช้ได้ — master จะเลือกต่อให้เอเจนต์ของตัวเองได้ภายในแถวเหล่านี้เท่านั้น',
      pickMaster: 'เลือก master',
      noMasters: 'ยังไม่มี master',
      partnerTitle: 'แถวที่ใช้ได้',
      partnerHint: 'ไม่ติ๊กเลย = ใช้ได้ทุกแถวที่เปิดใช้งาน',
      optionsEmpty: 'ไม่มีแถวให้เลือก — ผู้ดูแลระดับบนยังไม่เปิดแถวใดให้',
      nothingChosen: 'ยังไม่ได้เลือก — ใช้ได้ทุกแถวที่เปิดใช้งาน',
      membersSee: 'สมาชิกเห็นตอนนี้',
      blocked: 'แถวที่เลือกไม่เหลือให้สมาชิกแทงได้เลย — สมาชิกในสายนี้จะเข้าเล่น KENO ไม่ได้ แก้การเลือก หรือล้างเพื่อใช้ทุกแถว',
      save: 'บันทึก',
      clear: 'ล้างการเลือก',
      saved: 'บันทึกแล้ว',
      cleared: 'ล้างการเลือกแล้ว — ใช้ทุกแถวที่ได้รับอนุญาต',
      loadFailed: 'โหลดไม่สำเร็จ',
      agentCardTitle: 'วงเงิน KENO ของเอเจนต์นี้',
      agentCardHint: 'เลือกแถว “CHOOSE YOUR BET LIMIT” ที่สมาชิกของเอเจนต์นี้เลือกได้ — เลือกได้เฉพาะแถวที่คุณเองได้รับอนุญาต',
    },
    partnerWallet: {
      title: 'เงินค้างกับ partner',
      subtitle: 'รายการจ่ายรางวัล/คืนเงินให้สมาชิกของเอเย่นต์ seamless ที่ส่งให้ partner ไม่สำเร็จหรือกำลังลองส่งใหม่ — เงินของสมาชิกยังไม่อยู่ที่ที่ระบบเข้าใจ',
      filterAttention: 'ต้องดู',
      filterFailed: 'ส่งไม่สำเร็จ',
      filterRetrying: 'กำลังลองใหม่',
      filterAll: 'ทั้งหมด',
      summaryFailed: (n) => `ส่งไม่สำเร็จ ${n}`,
      summaryRetrying: (n) => `กำลังลองใหม่ ${n}`,
      colTime: 'เวลา',
      colKind: 'ประเภท',
      colMember: 'สมาชิก / เอเย่นต์',
      colAmount: 'จำนวน',
      colRef: 'อ้างอิง',
      colStatus: 'สถานะ',
      colError: 'สาเหตุล่าสุด',
      kindCredit: 'จ่ายรางวัล',
      kindRollback: 'คืนเงิน / ย้อนรายการ',
      kindSettle: 'แจ้งผล (ไม่มีเงิน)',
      statusFailed: 'ส่งไม่สำเร็จ',
      statusRetrying: 'กำลังลองใหม่',
      statusDone: 'ส่งแล้ว',
      statusCancelled: 'ปิดเรื่อง / ยกเลิก',
      statusPending: 'รอส่ง',
      attempts: (n) => `ลองแล้ว ${n} ครั้ง`,
      nextAttempt: 'ลองถัดไป',
      empty: 'ไม่มีรายการค้าง — เงินที่ต้องส่งให้ partner ไปถึงครบแล้ว',
      emptyFiltered: 'ไม่มีรายการในตัวกรองนี้',
      retry: 'ส่งใหม่',
      resolve: 'ปิดเรื่อง',
      reload: 'โหลดใหม่',
      drawRef: 'งวด',
      roundRef: 'รอบ',
      retryTitle: 'ส่งรายการนี้ให้ partner อีกครั้ง',
      retryDescription: (kind, amount, member) => `${kind} ${amount} บาท · สมาชิก ${member} — ระบบจะลองส่งทันที และเริ่มนับโควตาลองใหม่ตั้งแต่ตอนนี้`,
      retryConfirm: 'ส่งใหม่',
      resolveTitle: 'ปิดเรื่องที่แก้กับ partner แล้ว',
      resolveDescription: (kind, amount, member) => `${kind} ${amount} บาท · สมาชิก ${member} — ปิดแล้วระบบจะไม่ส่งรายการนี้อีก และปิดเหตุที่เปิดไว้`,
      resolveNoteLabel: 'หมายเหตุ — แก้กับ partner อย่างไร',
      resolveNoteHint: 'เช่น โอนมือให้สมาชิกแล้ว อ้างอิงตั๋ว TK-123',
      resolveNext: 'ต่อไป: ขอรหัสยืนยัน',
      resolveConfirm: 'ปิดเรื่อง',
      doneRetry: 'สั่งส่งใหม่แล้ว',
      doneResolve: 'ปิดเรื่องแล้ว',
    },
    settlements: {
      title: 'การชำระ',
      round: 'รอบ',
      totalBets: 'จำนวนเดิมพัน',
      totalBetAmount: 'ยอดเดิมพันรวม',
      totalWinAmount: 'ยอดชนะรวม',
      totalLossAmount: 'ยอดขาดทุนรวม',
      status: 'สถานะ',
      processedAt: 'ชำระเมื่อ',
      errorMessage: 'ข้อผิดพลาด',
      noSettlements: 'ไม่มีข้อมูลการชำระ',
    },
    reports: {
      title: 'รายงาน',
      winLoss: 'รายงานกำไร/ขาดทุน',
      from: 'ตั้งแต่',
      to: 'ถึง',
      agent: 'เอเจนต์',
      allAgents: 'ทุกเอเจนต์',
      generate: 'สร้างรายงาน',
      generating: 'กำลังสร้าง...',
      totalBet: 'ยอดเดิมพันรวม',
      betVolume: 'ยอดเดิมพัน',
      totalWin: 'ยอดชนะรวม',
      payout: 'ยอดจ่าย',
      netProfit: 'กำไรสุทธิ',
      agentNet: 'ส่วนแบ่งเอเจนต์',
      masterNet: 'ส่วนแบ่งมาสเตอร์',
      adminNet: 'ส่วนแบ่งแอดมิน',
      agentShare: 'หุ้น',
      masterShare: 'หุ้นมาสเตอร์',
      memberCount: 'จำนวนสมาชิก',
      roundCount: 'จำนวนรอบ',
      noData: 'กดค้นหาเพื่อดูรายงาน',
      noResults: 'ไม่มีข้อมูลในช่วงเวลานี้',
      results: 'ผลลัพธ์',
      totals: 'ยอดรวม',
      exportCsv: 'ส่งออก CSV',
      exporting: 'กำลังส่งออก…',
    },
    agents: {
      title: 'เอเจนต์',
      createAgent: 'เพิ่มเอเจนต์',
      code: 'รหัส',
      name: 'ชื่อ',
      commissionRate: 'สัดส่วนเอเจนต์',
      walletType: 'ประเภทกระเป๋าเงิน',
      status: 'สถานะ',
      isActive: 'เปิดใช้งาน',
      apiKey: 'API Key',
      rotateKey: 'หมุน Key',
      testConnection: 'ทดสอบการเชื่อมต่อ',
      connectionOk: 'เชื่อมต่อสำเร็จ',
      connectionFail: 'เชื่อมต่อล้มเหลว',
      noAgents: 'ไม่มีเอเจนต์',
      createError: 'สร้างเอเจนต์ไม่สำเร็จ',
      updateError: 'อัปเดตเอเจนต์ไม่สำเร็จ',
      internal: 'กระเป๋าภายใน',
      seamless: 'Seamless',
      walletConfig: 'ตั้งค่ากระเป๋าเงิน',
      walletUrl: 'URL กระเป๋าเงินเอเจนต์',
      walletApiKey: 'API Key กระเป๋าเงินเอเจนต์',
      walletApiKeyHint: 'ส่งเป็น X-Api-Key ไปยังเอเจนต์',
      walletHelpSeamless: 'เอเจนต์จัดการยอดเงินสมาชิกผ่าน wallet API ของตัวเอง',
      walletHelpInternal: 'Lotmatic จัดการยอดเงินภายในระบบ (ไม่เรียก wallet ภายนอก)',
      loginAccount: 'บัญชีเข้าสู่ระบบของเอเจนต์',
      loginAccountHint: 'เอเจนต์ใช้บัญชีนี้เข้าหน้าเอเจนต์ เพื่อดูสมาชิก รายงาน และ API key ของตัวเอง',
      loginCreatedHint: (host) => `ให้เอเจนต์เข้าสู่ระบบที่ ${host}`,
      walletOptionalHint: 'ยังไม่กรอกก็สร้างได้ ตั้งทีหลังได้ในหน้ารายละเอียดเอเจนต์ — สมาชิกของเอเจนต์ seamless จะแทงได้เมื่อตั้ง URL และ API key ครบ',
      walletNotSet: 'ยังตั้ง URL หรือ API key กระเป๋าเงินไม่ครบ — สมาชิกของเอเจนต์นี้ยังแทงไม่ได้',
      walletApiKeyKeep: 'เว้นว่างไว้ = ใช้ค่าเดิม',
      walletApiKeySet: (masked) => `ตั้งไว้แล้ว (${masked})`,
      resetPassword: 'ตั้งรหัสผ่านใหม่',
      resetPasswordHint: 'รหัสเดิมจะใช้เข้าสู่ระบบไม่ได้อีก — แจ้งรหัสใหม่ให้เอเจนต์เอง',
      newPassword: 'รหัสผ่านใหม่',
      resetPasswordDone: 'ตั้งรหัสผ่านใหม่แล้ว',
      noLoginAccount: 'เอเจนต์นี้ยังไม่มีบัญชีเข้าสู่ระบบ',
      resetPasswordError: 'ตั้งรหัสผ่านใหม่ไม่สำเร็จ',
      showPassword: 'แสดงรหัสผ่าน',
      hidePassword: 'ซ่อนรหัสผ่าน',
    },
    providers: {
      title: 'ผู้ให้ข้อมูล',
      subtitle: 'ตั้งค่าแหล่งราคา',
      code: 'รหัส',
      type: 'ประเภท',
      symbols: 'สัญลักษณ์',
      isActive: 'สถานะ',
      toggle: 'เปลี่ยนสถานะ',
      toggleError: 'เปลี่ยนสถานะไม่สำเร็จ',
      noProviders: 'ไม่มีผู้ให้ข้อมูล',
      enable: 'เปิดใช้',
      disable: 'ปิด',
      config: 'การตั้งค่า',
      typeCrypto: 'คริปโต',
      typeForex: 'ฟอเร็กซ์',
      typeGold: 'ทองคำ',
      typeStock: 'หุ้น',
      typeOil: 'น้ำมัน',
      typeIndex: 'ดัชนี',
      typeCustom: 'กำหนดเอง',
    },
    audit: {
      title: 'บันทึกกิจกรรม',
      actor: 'ผู้กระทำ',
      action: 'กิจกรรม',
      entity: 'เป้าหมาย',
      ipAddress: 'IP Address',
      detail: 'รายละเอียด',
      noLogs: 'ไม่มีบันทึก',
      viewDetail: 'ดูรายละเอียด',
    },
    docs: {
      title: 'คู่มือการเชื่อมต่อ',
      overview: 'ภาพรวม',
      integrationFlow: 'ขั้นตอนการเชื่อมต่อ',
      walletApi: 'Wallet API — Server ที่คุณต้องสร้าง',
      codeExamples: 'ตัวอย่างโค้ด',
      authSection: 'การยืนยันตัวตน',
      endpointsSection: 'Endpoints',
      agentCode: 'รหัสเอเจนต์',
      yourApiKey: 'Lotmatic API Key ของคุณ',
      apiBase: 'Lotmatic API Base URL',
      walletUrl: 'Wallet URL ของคุณ',
      overviewText: 'Lotmatic ใช้รูปแบบ Seamless Wallet — Server ของคุณเก็บยอดเงินสมาชิก เมื่อสมาชิกวางเดิมพันหรือชนะ Lotmatic จะเรียก wallet API ของคุณเพื่อหักหรือเติมเงิน',
      authText: 'ทุกคำขอจาก Lotmatic จะส่ง X-Api-Key: <your_wallet_api_key> ปฏิเสธด้วย 401 หากไม่ตรงกัน',
      method: 'วิธี',
      path: 'Path',
      description: 'คำอธิบาย',
      epBalance: 'ดูยอดเงินของสมาชิก',
      epDebit: 'หักเงินจำนวนเดิมพันจากสมาชิก',
      epCredit: 'เติมเงินรางวัลให้สมาชิก',
      epRollback: 'ยกเลิก debit หรือ credit',
      epSettle: 'แจ้งผลแพ้/ชนะของเดิมพัน (สำหรับ reconciliation เท่านั้น ไม่โยกเงิน)',
      balanceQueryParam: 'Query param: memberId — รหัสสมาชิกภายในของคุณ',
      debitIdempotent: 'หักจำนวนเงินเดิมพัน ต้องเป็น idempotent บน txId',
      rollbackDesc: 'ยกเลิก debit หรือ credit ที่ผ่านมา เรียกเมื่อ settlement ล้มเหลว',
      settleDesc: 'แจ้งผลสุดท้ายของเดิมพัน (ชนะหรือแพ้) เพื่อให้ฝั่งคุณ reconcile ยอด — endpoint นี้ไม่โยกเงิน เพราะ debit/credit จัดการเงินไปแล้ว เรียกทุกบิลทั้งชนะและแพ้ ถ้า endpoint นี้ตอบช้าหรือ down จะไม่กระทบ settlement ของ Lotmatic (มี retry ให้อัตโนมัติ)',
      flowStep0: 'สมาชิกลงทะเบียนบนแพลตฟอร์มของคุณ — Server เรียก POST {NEXBIT_API_BASE_URL}/api/external/create-member เพื่อขอ memberId จาก Lotmatic (ทำครั้งเดียวต่อสมาชิก ไม่สร้างผ่านหน้า Admin/Agent แล้ว)',
      flowStep1: 'สมาชิกกด "เล่น" บนแพลตฟอร์มของคุณ',
      flowStep2: 'Server ของคุณสร้าง launchToken (HMAC) เอง แล้วเรียก POST {NEXBIT_API_BASE_URL}/api/external/launch พร้อม X-Partner-Key header — ดูวิธีสร้าง token ด้านล่าง',
      flowStep3: 'ได้รับ accessToken + launchUrl กลับมา — redirect สมาชิกไปยัง launchUrl ที่ได้รับ (token อายุสั้นตาม JWT_EXPIRES_IN)',
      flowStep4: 'Lotmatic ตรวจสอบ token และโหลดเกมให้สมาชิก',
      flowStep5: 'เมื่อสมาชิกวางเดิมพัน Lotmatic เรียก /api/wallet/debit ของคุณ',
      flowStep6: 'เมื่อ settlement รอบ Lotmatic เรียก /api/wallet/credit สำหรับผู้ชนะ',
      flowStep7: 'หากเกิดข้อผิดพลาดระหว่างการดำเนินการ Lotmatic เรียก /api/wallet/rollback เพื่อยกเลิก debit',
      flowStep8: 'หลัง settlement เสร็จ Lotmatic เรียก /api/wallet/settle แจ้งผลแพ้/ชนะของทุกบิล (รวมบิลที่แพ้) เพื่อให้คุณ reconcile ยอด',
      launchTitle: 'การ Launch สมาชิก (สร้าง launchToken ด้วย HMAC)',
      launchExplain: 'launchToken ต้องสร้างโดย server ของคุณเอง ไม่ได้ขอจาก Lotmatic ก่อน — สร้างจาก nonce แบบสุ่ม แล้วเซ็นด้วย HMAC-SHA256 โดยใช้ Launch Secret ที่ Lotmatic มอบให้ (คนละตัวกับ Lotmatic API Key)',
      launchTokenFormat: 'รูปแบบ token: <nonce>.<hmac> โดย hmac = HMAC_SHA256(launchSecret, `${memberId}:${nonce}`) เป็น hex string',
      launchSecretLabel: 'Launch Secret',
      launchSecretNote: 'Launch Secret เป็นคนละตัวกับ Lotmatic API Key — ดู/หมุนได้จากหน้า Agent Detail (แสดงเต็มครั้งเดียวตอนสร้างหรือหมุนใหม่เท่านั้น) ห้ามฝัง secret นี้ในโค้ด client-side เด็ดขาด ต้องสร้าง token ฝั่ง server ของคุณเท่านั้น',
      launchGenerateHeading: 'ขั้นตอนสร้าง launchToken',
      launchRequestHeading: 'เรียก Lotmatic เพื่อแลก token',
      launchResponseHeading: 'Response ที่ได้กลับมา',
      createMemberTitle: 'การสร้างสมาชิก (Member Provisioning)',
      createMemberExplain: 'สมาชิกทุกคนต้องถูกสร้างผ่าน API นี้เท่านั้น — ไม่สามารถสร้างจากหน้า Admin/Agent ได้แล้ว ส่ง username และ displayName มา Lotmatic จะตรวจสอบว่ามีสมาชิกนี้ในสังกัดของคุณหรือยัง ถ้ายังไม่มีจะสร้างให้ก่อน แล้วตอบกลับ memberId ที่ไม่ซ้ำกัน — เก็บค่านี้ไว้อ้างอิงฝั่งคุณ',
      createMemberIdempotentNote: 'Idempotent: เรียกซ้ำด้วย username เดิม จะได้ memberId เดิมกลับมา (ไม่สร้างซ้ำ) ปลอดภัยสำหรับ retry เมื่อ network timeout',
      createMemberRequestHeading: 'เรียกสร้างสมาชิก',
      createMemberResponseHeading: 'Response ที่ได้กลับมา',
      kenoLaunchTitle: 'เข้าเกม KENO',
      kenoLaunchExplain: 'KENO อยู่คนละหน้าเว็บกับหวย แต่ใช้สมาชิก กระเป๋าเงิน และ Wallet API ชุดเดียวกัน — สร้าง launchToken แบบเดียวกันทุกอย่าง ต่างแค่เรียก /api/external/keno/launch แทน /api/external/launch แล้ว launchUrl ที่ได้จะพาสมาชิกเข้าหน้า KENO ส่วน gameCode ใน debit/settle จะเป็นรหัสโต๊ะ เช่น keno_earth (keno_jupiter, keno_mars, keno_neptune)',
      drawLotteryTitle: 'หวย (หวยรัฐบาล หวยหุ้น ฯลฯ)',
      drawLotteryExplain: 'หวยแบบงวดใช้ Wallet API ชุดเดียวกัน และเข้าเกมด้วย /api/external/launch เหมือนเดิม gameCode คือรหัสหวย เช่น government และ roundId คือรหัสงวด หน่วยของเงินคือ "บิล" ที่สมาชิกกดส่งครั้งเดียว: 1 บิล = 1 รายการ /debit (ยอดรวมทุกเลขในบิล) · บิลที่ชนะ = 1 รายการ /credit (รวมรางวัลทุกเลขที่ชนะในบิลนั้น) · ผลของแต่ละบิลแจ้งด้วย /settle',
      drawLotteryMoneyNote: 'เงินรางวัลและเงินคืนส่งแบบไม่รอ: ระบบบันทึกคำสั่งพร้อมกับการตัดผล แล้วส่งให้ฝั่งคุณภายในไม่กี่วินาที ถ้าฝั่งคุณไม่ตอบจะลองใหม่ต่อเนื่องประมาณ 12 ชั่วโมง ทุกคำสั่งใช้ txId เดิมซ้ำเสมอ ฝั่งคุณจึงต้องตอบซ้ำด้วยผลเดิมโดยไม่หักหรือเติมเงินซ้ำ การคืนเงินบิลของงวดที่ยกเลิก/งดออกผล และการแก้ผลหลังจ่ายแล้ว ส่งเป็น /rollback โดย originalTxId คือ txId ของ /debit หรือ /credit เดิม การย้อนรางวัลที่สมาชิกใช้ไปแล้วฝั่งคุณอาจปฏิเสธ (ยอดไม่พอ) ซึ่งเราจะเปิดเป็นเหตุให้เจ้าหน้าที่แก้ด้วยมือ',
      kenoRefundNote: 'KENO มีกรณีคืนเงินเต็มจำนวน: แทงมังกร/เสือแล้วผลออก DT-เท่ากัน ระบบจะเรียก /settle ด้วย result "won" และ winAmount เท่ากับยอดแทง (คืนทุน ไม่มีกำไร) — ฝั่งคุณจึงไม่ต้องรองรับผลแบบใหม่',
    },
    detail: {
      backToAgents: 'เอเจนต์',
      agentNotFound: 'ไม่พบเอเจนต์',
      loadFailed: 'โหลดข้อมูลเอเจนต์ไม่สำเร็จ',
      integrationDocs: 'คู่มือการเชื่อมต่อ',
      credentials: 'ข้อมูลรับรอง',
      agentCode: 'รหัสเอเจนต์',
      apiKeyMasked: 'Lotmatic API Key (ปิดบัง)',
      newApiKeyNotice: 'API Key ใหม่ — คัดลอกตอนนี้:',
      newApiKeyLabel: 'Lotmatic API Key ใหม่',
      rotateApiKey: 'หมุน API Key',
      rotateApiKeyHint: 'Key เดิมจะใช้ไม่ได้ทันที',
      launchSecretMasked: 'Launch Secret (ปิดบัง)',
      newLaunchSecretNotice: 'Launch Secret ใหม่ — คัดลอกตอนนี้:',
      newLaunchSecretLabel: 'Launch Secret ใหม่',
      rotateLaunchSecret: 'หมุน Launch Secret',
      rotateLaunchSecretHint: 'ใช้สำหรับเซ็น launchToken — secret เดิมจะใช้ไม่ได้ทันที ต้องอัปเดตฝั่ง agent ด้วย',
      testing: 'กำลังทดสอบ...',
      testConnection: 'ทดสอบการเชื่อมต่อ',
      connected: 'เชื่อมต่อสำเร็จ',
      connectionFailed: 'เชื่อมต่อล้มเหลว',
      requestFailed: 'คำขอล้มเหลว',
      settings: 'การตั้งค่า',
      displayName: 'ชื่อที่แสดง',
      nameRequired: 'กรุณาระบุชื่อ',
      commissionRate: 'สัดส่วนเอเจนต์ (%)',
      commissionRateHint: '(0–100, เช่น 90 = เอเจนต์ได้ 90%)',
      commissionRateInvalid: 'ต้องเป็น 0–100 (เช่น 90 สำหรับ 90%)',
      status: 'สถานะ',
      activeLabel: 'เปิดใช้งาน (สมาชิกเล่นได้)',
      walletType: 'ประเภทกระเป๋าเงิน',
      agentWalletUrl: 'Wallet URL ของเอเจนต์',
      walletUrlInvalid: 'ต้องเป็น URL ที่ถูกต้อง',
      agentWalletApiKey: 'Wallet API Key ของเอเจนต์',
      agentWalletApiKeyHint: '(เว้นว่างเพื่อใช้ค่าเดิม)',
      showKey: 'แสดง',
      hideKey: 'ซ่อน',
      saveChanges: 'บันทึกการเปลี่ยนแปลง',
      saving: 'กำลังบันทึก...',
      saveSuccess: 'บันทึกสำเร็จ',
      saveFailed: 'บันทึกไม่สำเร็จ',
      copyLabel: 'คัดลอก',
    },
  },

  en: {
    lang: {
      toggle: 'TH',
      toggleLabel: 'Toggle language',
      th: 'Thai',
      en: 'English',
    },
    nav: {
      kenoLimits: 'KENO limits',
      partnerWallet: 'Partner wallet',
      dashboard: 'Dashboard',
      rounds: 'Rounds',
      bets: 'Bets',
      members: 'Members',
      games: 'Games',
      settlements: 'Settlements',
      reports: 'Reports',
      masters: 'Masters',
      admins: 'Admins',
      agents: 'Agents',
      providers: 'Providers',
      auditLog: 'Audit Log',
      ipWhitelist: 'IP Whitelist',
      management: 'Management',
      profile: 'Profile',
      numberLimits: 'Number limits',
      myNumberLimits: 'Number limits',
      numberLimitHistory: 'Limit history',
      payoutRates: 'Payout rates',
      settings: 'Settings',
      banners: 'Home banners',
      keys: 'API Keys',
      docs: 'Docs',
      draws: 'Draws',
      staging: 'Staging',
      stagingTest: 'Run tests',
      stagingRuns: 'Test history',
      stagingReports: 'Staging reports',
    },
    auth: {
      signIn: 'Sign In',
      signInSubtitle: 'Admin Console',
      username: 'Username',
      password: 'Password',
      showPassword: 'Show password',
      hidePassword: 'Hide password',
      submitting: 'Signing in...',
      adminOnly: 'Admin access only',
      usernameRequired: 'Please enter username',
      passwordRequired: 'Please enter password',
      invalidCredentials: 'Invalid username or password',
      networkError: 'Connection failed. Please try again',
    },
    header: {
      history: 'History',
      logout: 'Log out',
    },
    common: {
      loading: 'Loading...',
      error: 'An error occurred',
      retry: 'Retry',
      save: 'Save',
      saving: 'Saving...',
      cancel: 'Cancel',
      close: 'Close',
      create: 'Create',
      creating: 'Creating...',
      edit: 'Edit',
      delete: 'Delete',
      actions: 'Actions',
      status: 'Status',
      createdAt: 'Created',
      updatedAt: 'Updated',
      page: 'Page',
      of: 'of',
      prev: 'Prev',
      next: 'Next',
      total: 'Total',
      items: 'items',
      noData: 'No data',
      confirm: 'Confirm',
      success: 'Success',
      refresh: 'Refresh',
      search: 'Search',
      filter: 'Filter',
      all: 'All',
      active: 'Active',
      inactive: 'Inactive',
      suspended: 'Suspended',
      yes: 'Yes',
      no: 'No',
      username: 'Username',
      password: 'Password',
      displayName: 'Display Name',
      optional: 'optional',
      createdShort: 'Created',
      adminConsole: 'Admin Console',
      masterConsole: 'Master Console',
      agentConsole: 'Agent Console',
      balance: 'Balance',
    },
    dashboard: {
      title: 'Dashboard',
      totalMembers: 'Total Members',
      activeMembers: 'Active Members',
      betsToday: 'Bets Today',
      betVolume: 'Bet Volume',
      payouts: 'Payouts',
      houseEdge: 'House Edge',
      openRounds: 'Open Rounds',
      statsError: 'Failed to load stats',
      maintenanceMode: 'Maintenance Mode',
      maintenanceActive: 'Maintenance active — members cannot bet',
    },
    members: {
      title: 'Members',
      createMember: 'Create Member',
      username: 'Username',
      displayName: 'Display Name',
      role: 'Role',
      status: 'Status',
      wallet: 'Wallet',
      loginAs: 'Login As',
      balance: 'Balance',
      credit: 'Credit',
      creditAmount: 'Amount',
      creditNote: 'Note',
      creditSuccess: 'Credit successful',
      impersonateError: 'Impersonate failed',
      chooseGameTitle: (u) => `Login as "${u}"`,
      chooseGameDescription: 'Choose which site to open in a new tab',
      gameLottery: 'Lottery',
      gameLotteryHint: 'Government, stock and price lotteries',
      gameKeno: 'KENO',
      gameKenoHint: 'KENO, 4 tables',
      walletError: 'Failed to load wallet',
      createError: 'Failed to create member',
      noMembers: 'No members found',
      confirmImpersonate: (u) => `Log in as "${u}"?`,
      createdViaApiNotice: 'Members are created via API only (POST /api/external/create-member)',
      suspend: 'Suspend',
      unsuspend: 'Unsuspend',
      suspendSuccess: 'Status updated',
      suspendError: 'Failed to update status',
    },
    betReport: {
      title: 'Bet Report',
      member: 'Member',
      betCount: 'Bets',
      betVolume: 'Bet Volume',
      payout: 'Payout',
      net: 'Net',
      viewBets: 'View Bets',
      memberBets: 'Member Bets',
      noActivity: 'No activity in this period',
      backToReport: 'Back to Report',
      betType: 'Type',
      result: 'Result',
      game: 'Game',
      date: 'Date',
    },
    numberCaps: {
      title: 'Per-number ceilings',
      desc: 'Give one number a ceiling different from its bet type’s default — higher or lower. ' +
        'Numbers you do not set keep the default. Applies to this draw and every one after it until removed.',
      addNumbers: (digits) => `Numbers (${digits} digits) — several ok`,
      addChoice: 'Choice',
      cap: 'Ceiling per number',
      capPlaceholderHint: (def) => `default ${def}`,
      set: 'Set ceiling',
      worstCase: (amount) => `If this number comes up the house pays up to ${amount}`,
      colNumber: 'Number',
      colCap: 'This number’s ceiling',
      colVsDefault: 'Vs default',
      colTaken: 'Taken',
      colLoss: 'Payout if hit',
      lossHint: 'What the house pays if this number comes up = stake taken × the bet type’s payout rate',
      lossIfFull: (amount) => `At the ceiling ${amount}`,
      sortHint: (column) => `Sort by ${column}; press again to flip the direction`,
      typeDefault: (def) => `default ${def}`,
      higher: (diff) => `higher +${diff}`,
      lower: (diff) => `lower −${diff}`,
      same: 'same as default',
      overPool: (pool) => `Above the type’s total pool (${pool}) — held to the pool`,
      addToType: 'Add to this type',
      editing: 'editing above',
      edit: 'Edit ceiling',
      remove: 'Remove (back to default)',
      empty: 'No number has its own ceiling — every number uses its bet type’s default',
      loadError: 'Could not load the per-number ceilings',
      badDigits: (digits, bad) => `This type takes ${digits}-digit numbers — fix: ${bad}`,
      badChoice: (bad) => `This type takes only high / low / odd / even — fix: ${bad}`,
      belowMin: (min) => `The ceiling cannot be below the minimum bet (${min}) — to stop taking a number, close it`,
      saved: (n) => `Ceiling set on ${n} number(s)`,
      otpTitle: 'Confirm raising a ceiling',
      otpConfirm: 'Confirm raise',
      otpSetDesc: (n, list) =>
        `This raises the ceiling on ${n} number(s) (${list}). Raising adds risk, so it needs a code; lowering does not.`,
      otpRemoveDesc: (number, now, def) =>
        `This removes the ceiling on ${number} (now ${now}); it goes back to the default ${def}, which is higher, so it needs a code.`,
      otpLimitsDesc: (type, list) =>
        `This raises the ceilings of ${type} (${list}). Raising adds risk, so it needs a code; lowering does not.`,
    },
    agentNumberRules: {
      title: 'Number limits',
      desc: 'Close numbers, cap intake and cut payouts — for your own members only, never another agent’s',
      carriesOver: 'Rules apply to this draw and every one after it — they do not clear themselves when a draw ends',
      clearAll: 'Clear all',
      clearTitle: 'Clear all number limits?',
      clearDesc: (count, lottery) =>
        `This removes all ${count} rules for ${lottery} (every bet type, whole-type rules included). ` +
        'Your members go back to the house limits immediately, and it cannot be undone.',
      clearConfirm: 'Yes, clear all',
      closeNumbers: 'Close numbers',
      setLimit: 'Set limit',
      wholeType: 'Whole bet type',
      housePayout: 'House payout',
      typePayout: 'Whole-type payout',
      myPayout: 'My payout',
      myCap: 'My cap',
      effCap: 'Cap in force',
      effPayout: 'Payout in force',
      fromHouse: 'house',
      fromType: 'whole type',
      myNumbers: 'What is set',
      addTitle: 'Add numbers',
      manageType: 'Manage this type',
      editingAbove: 'editing above',
      typeRuleOnly: 'Set on the whole type — no individual numbers yet',
      mineVsHouse: 'Mine / house',
      blocked: 'Closed',
      block: 'Close this number',
      unblock: 'Reopen this number',
      blockType: 'Close whole type',
      unblockType: 'Reopen whole type',
      addNumbers: (digits) => `Numbers (${digits} digits) — several ok`,
      addChoice: 'Choice',
      addHint: 'Numbers alone, then Close numbers · or add a cap/payout and press Set limit (still bettable, but limited) · an empty box keeps the greyed value shown',
      empty: 'No rules set, and nothing taken on this bet type this draw',
      clamped: (asked, actual) => `Set to ${asked} · in force ${actual} (the house lowered its own)`,
      badDigits: (digits, bad) => `This type takes ${digits}-digit numbers — fix: ${bad}`,
      badChoice: (bad) => `This type takes only high / low / odd / even — fix: ${bad}`,
    },
    agentSettings: {
      title: 'Settings',
      maintenanceMode: 'Maintenance Mode',
      maintenanceDesc: 'When enabled, your members cannot place bets (does not affect other agents)',
      maintenanceOn: 'Enable Maintenance',
      maintenanceOff: 'Disable Maintenance',
      toggleSuccess: 'Status updated',
      toggleError: 'Failed to update status',
      walletApiKeyTitle: 'Wallet API Key',
      walletApiKeyDesc: 'The key your own server checks Lotmatic\'s requests against (X-Api-Key) — you generate this, Lotmatic does not.',
      walletApiKeyLabel: 'Wallet API Key',
      walletApiKeyPlaceholder: 'At least 16 characters',
      walletApiKeyCurrentNotSet: 'Not set',
      walletApiKeySave: 'Save',
      walletApiKeySaving: 'Saving...',
      walletApiKeySaveSuccess: 'Wallet API Key saved',
      walletApiKeySaveError: 'Failed to save',
    },
    agentKeys: {
      title: 'API Keys',
      nexbitApiKey: 'Lotmatic API Key',
      nexbitApiKeyDesc: 'Send in X-Partner-Key header when calling /api/external/*',
      launchSecret: 'Launch Secret',
      launchSecretDesc: 'Used to sign launchToken (HMAC-SHA256) — server-side only',
      rotate: 'Rotate Key',
      rotating: 'Rotating...',
      rotateSuccess: 'Key rotated',
      rotateWarning: 'Current key invalidated immediately — update your server too',
      newKeyNotice: 'New key — copy now:',
      copyNow: 'Copy',
    },
    agentPassword: {
      title: 'Change Password',
      current: 'Current Password',
      newPassword: 'New Password',
      confirm: 'Confirm New Password',
      mismatch: 'Passwords do not match',
      tooShort: 'Password must be at least 8 characters',
      success: 'Password changed successfully',
      wrong: 'Current password is incorrect',
      change: 'Change Password',
      changing: 'Changing...',
    },
    games: {
      title: 'Games',
      subtitle: 'Game and payout configuration',
      config: 'Game Configuration',
      unit: 'games',
      gameCode: 'Game Code',
      minBet: 'Min Bet',
      maxBet: 'Max Bet',
      duration: 'Duration',
      betClose: 'Bet Close Before',
      cooldown: 'Cooldown',
      payoutHigh: 'Payout High',
      payoutLow: 'Payout Low',
      payoutOdd: 'Payout Odd',
      payoutEven: 'Payout Even',
      highThreshold: 'High Threshold',
      isActive: 'Active',
      saveSuccess: 'Settings saved',
      saveError: 'Failed to save',
      noGames: 'No games',
      seconds: 's',
      secShort: 's',
      tradingSchedule: 'Trading Schedule',
      roundSec: 'Round (sec)',
      closeBeforeSec: 'Close Before (sec)',
      highX: 'High ×',
      lowX: 'Low ×',
      oddX: 'Odd ×',
      evenX: 'Even ×',
      metaRound: 'round',
      metaClose: 'close',
      metaPayout: 'payout',
    },
    draws: {
      title: 'Draw Lottery',
      lotteries: 'Lotteries',
      lotteryList: 'Lottery List',
      selectLottery: 'Select a lottery to view its draws',
      category: 'Category',
      categoryDraw: 'Draw',
      categoryStock: 'Stock',
      recentDraws: 'Recent Draws',
      drawNumber: 'Draw #',
      drawDate: 'Draw Date',
      status: 'Status',
      closeAt: 'Closes',
      noLotteries: 'No lotteries',
      noDraws: 'No draws',
      statusScheduled: 'Scheduled',
      statusOpen: 'Open',
      statusClosed: 'Closed',
      statusResulted: 'Resulted',
      statusSettled: 'Settled',
      statusCancelled: 'Cancelled',
      statusSkipped: 'No result',
      skipDraw: 'No result',
      skipTitle: 'Announce no result for this draw',
      skipDesc: 'For a draw that will never announce (a market holiday). Every bet is refunded immediately, the draw stays visible to members reading "งดออกผล", and the scheduler will not re-open this round.',
      skipConfirm: 'Confirm no result & refund',
      skipDone: (n, amount) => `Marked no result — refunded ${n} slips, ฿${amount} total`,
      skipError: 'Failed to mark no result',
      cancelDraw: 'Cancel draw',
      cancelTitle: 'Cancel draw and refund',
      cancelDesc: 'For a draw that will never have a result — every bet is voided and refunded to the player straight away. This cannot be undone.',
      cancelReason: 'Reason',
      cancelReasonHint: 'Recorded in the audit log, e.g. market did not trade',
      cancelConfirm: 'Confirm cancel and refund',
      cancelDone: (n, amount) => `Draw cancelled — refunded ${n} bet(s), ฿${amount}`,
      cancelError: 'Could not cancel the draw',
      bettingOpen: 'Betting still open',
      readyForResult: 'Betting closed — ready for result',
      result: 'Result',
      enterResult: 'Enter Result',
      editResult: 'Edit Result',
      settle: 'Settle',
      voidAction: 'Void',
      payout: 'Payout',
      payoutTitle: 'Payout & Stake Limits',
      payoutDesc:
        'Edit this lottery\'s payout rates and stake limits. Existing bets keep the rate they were placed at; changes apply to bets made after saving.',
      payoutSubmit: 'Confirm & Save',
      payoutError: 'Failed to save payout settings',
      payoutSaved: 'Payout settings saved',
      payoutOpenWarning:
        'A draw is taking bets right now — players see the new rates as soon as you save. Bets already placed still pay at their original rate.',
      payoutBetType: 'Bet type',
      payoutEnabled: 'Enabled',
      payoutRate: 'Payout',
      payoutMin: 'Min',
      payoutMax: 'Max/bet',
      payoutPerNumber: 'Max/number',
      payoutPerType: 'Cap/type',
      exposure: 'Intake',
      exposureTitle: 'Draw intake',
      exposureDesc: 'Stake taken on this draw against the configured caps. Refreshes every 5 seconds.',
      exposureEmpty: 'No bets on this draw yet',
      exposureTaken: 'taken',
      exposureBets: 'bets',
      exposureFull: 'full',
      exposureTopNumbers: 'Biggest numbers',
      exposureAuto: 'auto-refresh',
      blockTitle: 'Blocked numbers',
      blockPageDesc: 'Set intake caps per bet type, watch the stake on every number, and close the hot ones.',
      limitsTitle: 'Intake caps',
      limitsNoConfig: 'This bet type is not configured for this lottery',
      limitsMaxBetHint: 'per single bet',
      limitsPerNumberHint: 'per number, per draw',
      limitsPerTypeHint: 'all numbers of this type, per draw',
      limitsSaved: 'Saved',
      limitsNoBets: 'No bets on this type in this draw yet',
      limitsNumber: 'Number',
      limitsFill: 'Fill',
      limitsClosed: 'blocked',
      limitsLiability: 'Payout if hit',
      limitsWorstCase: 'Worst single number',
      limitsWorstNet: 'Net loss',
      limitsWorstHint: '(net of all stake taken on this type — only one number can win)',
      blockNoLiveDraw: 'No draw is taking bets — only all-draw blocks apply',
      blockHint: 'Blocked numbers stop taking bets immediately; bets already accepted still pay.',
      blockAdd: 'Block',
      blockEmpty: 'No numbers blocked on this draw',
      blockAllDraws: 'every draw',
      blockAllBadge: 'all draws',
      blockRemove: 'Unblock',
      payoutCeiling: (max) => `up to ×${max}`,
      payoutInvalid: (types) => `Invalid values: ${types}`,
      payoutChanges: 'Pending changes',
      payoutPassword: 'Your password',
      payoutPasswordHint: 'Confirms it is you — repeated wrong entries are temporarily blocked.',
      betTypeLabel: {
        '3top': 'Top 3',
        '3tote': 'Tote 3',
        '3front': 'Front 3',
        '3bottom': 'Bottom 3',
        '2top': 'Top 2',
        '2bottom': 'Bottom 2',
        run_top: 'Run top',
        run_bottom: 'Run bottom',
        odd_even_top: 'Odd/Even top',
        high_low_top: 'High/Low top',
        odd_even_bottom: 'Odd/Even bottom',
        high_low_bottom: 'High/Low bottom',
      },
      settings: 'Settings',
      settingsTitle: 'Lottery Settings',
      settingsDesc:
        'Set the draw timetable once — the next draw\'s times are worked out from it automatically.',
      settingsError: 'Failed to save settings',
      settingsSaved: 'Lottery settings saved',
      rules: 'Rules',
      rulesTitle: (name: string) => `Rules — ${name}`,
      rulesHint:
        'How this lottery draws its result, and the conditions that apply. Members read this beside the payout table, which is generated from the configured rates rather than typed here.',
      rulesTabEdit: 'Edit',
      rulesTabPreview: 'Preview',
      rulesLoadError: 'Could not load the rules',
      rulesSaveError: 'Could not save the rules',
      rulesSaved: 'Rules saved',
      scheduleType: 'Draw cycle',
      scheduleTypeLabel: {
        daily: 'Daily',
        weekly: 'Weekly',
        monthly: 'Monthly',
        manual: 'Manual',
      },
      scheduleDays: 'Days (multiple allowed)',
      scheduleDates: 'Dates of month (multiple allowed)',
      scheduleDatesHint: 'Months without that date are skipped — e.g. 31 skips February.',
      scheduleTime: 'Result time (ICT)',
      scheduleTimeHint: 'Thai time, no DST',
      addLottery: 'Add lottery',
      addLotteryDone: (name) => `${name} created — it is switched off; enable it from the list.`,
      scheduleRounds: (n) => `${n} rounds`,
      roundAdd: 'Add round',
      roundLabelPlaceholder: 'Round name, e.g. Morning',
      roundTimeOf: (n) => `Result time, round ${n}`,
      roundLabelOf: (n) => `Name of round ${n}`,
      roundRemoveOf: (n) => `Remove round ${n}`,
      roundDuplicate: 'Same time as another round — each round needs its own time',
      roundLabelRequired: 'A lottery with several rounds a day needs every round named',
      roundTimeInvalid: 'Invalid time',
      roundOpenOf: (n) => `Betting opens, round ${n}`,
      roundOpenLabel: 'Opens',
      roundOpenInherit: "Uses the lottery's opening time below",
      roundOpenInvalid: 'Invalid opening time',
      roundOpenAfterClose: 'Betting must open before it closes, or this draw can never take a bet',
      roundOpenComputed: (duration) => `≈ ${duration} before this round's result`,
      roundOpenModeInherit: 'Lottery default',
      roundOpenModeTime: 'Clock time',
      roundOpenModeAfterPrev: 'After previous result',
      roundOpenAfterPrevOf: (n) => `Opens after the previous result, round ${n}`,
      roundOpenAfterPrevHint: "Measured from the previous round's result (hh:mm)",
      roundOpenAfterPrevComputed: (duration) => `≈ ${duration} after the previous result`,
      roundOpenAfterPrevNow: 'Opens the moment the previous round is announced',
      roundOpenAfterPrevTooLate: 'Too late — it would fall past this round’s cutoff, leaving no time to bet',
      createAfterEnding: 'Open the next draw now?',
      createAfterCancelNote: 'Draw cancelled — it ended without a payout, so no next draw has been opened',
      createAfterSkipNote: 'Result skipped — the draw ended without a payout, so no next draw has been opened',
      roundsMax: (n) => `Up to ${n} rounds a day`,
      roundsPreview: (list) => `Will be saved as: ${list}`,
      roundsChangeNote: 'Changes apply to draws opened from now on; draws already open are untouched.',
      round: 'Round',
      scheduleDaily: 'Every day',
      scheduleManual: 'Manual — no timetable',
      scheduleManualHint: 'With Manual you enter the times each time you open a draw.',
      scheduleMonthlyOn: (dates) => `Day ${dates} of each month`,
      weekdayShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
      closeBefore: 'Close betting before result (minutes)',
      closeBeforeHint: '0–1440 minutes',
      openBefore: 'Open betting before result',
      openBeforeOff: 'Immediately',
      openBeforeDays: 'N days before',
      openBeforeTime: 'Fixed time',
      openBeforeOffHint: 'A new งวด takes bets the moment it opens — the default for most lotteries.',
      openBeforeDaysHint: '0–60 days, e.g. 10 for หวยไทย',
      openBeforePlaceholder: '10',
      // Whenever a งวด announces, not "every day" — a Sun/Wed/Sat lottery only opens on
      // those days, not the other four.
      openBeforeTimeHint: 'Betting opens at this clock time whenever a งวด announces, ahead of the result.',
      openBeforeTimeComputed: (duration) => `≈ ${duration} before the result`,
      openBeforeTimeRoundNote: (time) =>
        `Computed from the first round's time (${time}); used by rounds with no opening time of their own`,
      openAfterPrevious: 'Open sooner, after the previous round',
      openAfterPreviousHint:
        'Used when the previous round was within ~1.5 days — otherwise falls back to "Fixed time" above. For a lottery with an unusually long close-before, e.g. หวยหุ้นดาวโจนส์ (closes 7h ahead).',
      openAfterPreviousComputed: (duration) => `≈ ${duration} after the previous round`,
      closeBeforeShort: (mins) => `closes ${mins} min before`,
      createDraw: 'Open Next Draw',
      createTitle: 'Open Next Draw',
      createDesc:
        'These times come from this lottery\'s schedule. Review them and confirm to open the draw.',
      createDescManual:
        'Enter this draw\'s times by hand (for lotteries without a timetable, or a one-off round).',
      createEditTimes: 'Edit times manually',
      createSubmit: 'Open Draw',
      creating: 'Opening...',
      createError: 'Failed to open the draw',
      createSuccess: 'Next draw opened',
      createOrderInvalid: 'Order must be: open < close ≤ result',
      createBlocked: (n, closeAt) =>
        `Cannot open a new draw — draw #${n} is still taking bets (closes ${closeAt}). Wait for it to close.`,
      createStartsOpen: 'This draw starts accepting bets as soon as you confirm',
      createStartsScheduled: 'This draw stays "not open yet" until its open time, then opens automatically',
      openAt: 'Betting Opens',
      openAtHint: 'When members can start betting',
      closeAtHint: 'When bets stop being accepted',
      drawAt: 'Result Time',
      drawAtHint: 'When this draw is announced',
      clockNoDraw: 'No draw yet',
      clockAwaitingResult: 'Awaiting result',
      clockAwaitingPayout: 'Awaiting payout',
      unitDay: 'd',
      unitHour: 'h',
      unitMin: 'm',
      marketOpen: 'Market Open',
      marketClosed: 'Market Closed',
      marketToggleError: 'Failed to switch the market',
      closeBetting: 'Close Betting',
      closeConfirmTitle: 'Confirm Close',
      closeConfirmDesc:
        'Stops betting on this draw immediately, ahead of its scheduled close. Bets already submitted are kept, but anyone mid-slip can no longer submit — closed by mistake with no result entered yet? You can reopen it.',
      closeConfirmSubmit: 'Confirm & Close',
      closing: 'Closing...',
      closeError: 'Failed to close betting',
      closeSuccess: 'Betting closed — you can enter the result now',
      reopenBetting: 'Reopen Betting',
      reopening: 'Reopening...',
      reopenError: 'Failed to reopen betting',
      reopenSuccess: 'Betting reopened',
      prizeFirst: 'First Prize',
      topThree: 'Top 3',
      topOnlyNote: 'This lottery announces the top 3 only — no bottom two (bottom bets are disabled)',
      bottomTwo: 'Bottom Two',
      prizeFirstHintExact: '{n} digits',
      bottomTwoHint: '2 digits',
      prizeFirstInvalidExact: 'Must be exactly {n} digits',
      bottomTwoInvalid: 'Bottom two must be exactly 2 digits',
      frontThree: 'Front 3 digits',
      bottomThree: 'Last 3 digits',
      threeHint: 'Two numbers, 3 digits each',
      threeInvalid: 'Both numbers must be exactly 3 digits',
      submitResult: 'Enter Result',
      saveEdit: 'Save Changes',
      submitting: 'Submitting...',
      submitError: 'Failed to submit result',
      enterSuccess: 'Result recorded — review it, then settle',
      editSuccess: 'Result updated',
      settleConfirmTitle: 'Confirm Settlement',
      settleConfirmDesc:
        'This pays out winners for every bet in this draw based on the result below, immediately. Double-check it before confirming — if wrong, you can still Void to claw the payouts back afterwards.',
      settleConfirmSubmit: 'Confirm & Settle',
      settling: 'Settling...',
      settleError: 'Failed to settle',
      voidConfirmTitle: 'Confirm Void',
      voidConfirmDesc:
        'This claws back the payouts from every winner and returns the draw to the "Resulted" state so you can correct the result and settle again.',
      voidConfirmSubmit: 'Confirm & Void',
      voiding: 'Voiding...',
      voidError: 'Failed to void',
      resultSummary: (settled, won, lost) =>
        `Settled ${settled} bets — ${won} won · ${lost} lost`,
      voidSummary: (reversed, totalReturned) =>
        `Reversed ${reversed} bets — ${totalReturned} returned`,
    },
    rounds: {
      title: 'Rounds',
      subtitle: 'All game round history',
      history: 'Round History',
      roundNo: 'Round #',
      game: 'Game',
      status: 'Status',
      openAt: 'Open',
      closeAt: 'Close',
      settleAt: 'Settle At',
      result: 'Result',
      noRounds: 'No rounds match the filter',
      filterGame: 'Filter by game',
      filterStatus: 'Filter by status',
      allGames: 'All Games',
      allStatus: 'All Status',
      sum: 'Sum',
      highLow: 'High/Low',
      oddEven: 'Odd/Even',
      statusOpen: 'Open',
      statusClosed: 'Closed',
      statusSettling: 'Settling',
      statusSettled: 'Settled',
      statusCancelled: 'Cancelled',
      high: 'High',
      low: 'Low',
      odd: 'Odd',
      even: 'Even',
      unit: 'rounds',
    },
    bets: {
      title: 'Bets',
      subtitle: 'All member bet history',
      history: 'Bet History',
      member: 'Member',
      game: 'Game',
      gameRound: 'Game / Round',
      round: 'Round',
      type: 'Type',
      amount: 'Amount',
      payout: 'Payout',
      winAmount: 'Win',
      status: 'Status',
      time: 'Time',
      rollback: 'Rollback',
      rollbackConfirm: 'Confirm Rollback',
      rollbackSuccess: 'Rollback successful',
      rollbackError: 'Rollback failed',
      noBets: 'No bets found',
      filterStatus: 'Filter by status',
      allStatus: 'All Status',
      roundIdPlaceholder: 'Search by round ID...',
      statusPending: 'Pending',
      statusWon: 'Won',
      statusLost: 'Lost',
      statusCancelled: 'Cancelled',
      statusRefunded: 'Refunded',
      typeHigh: 'High',
      typeLow: 'Low',
      typeOdd: 'Odd',
      typeEven: 'Even',
    },
    kenoLimits: {
      title: 'KENO bet limits',
      subtitle: 'The rows of “CHOOSE YOUR BET LIMIT” on KENO — add, edit or suspend them, then choose which rows each master / agent offers its members',
      reload: 'Reload',
      houseTitle: 'All limits',
      houseHint: 'A suspended row is offered to nobody and a bet sent with it is refused — bets already placed are not affected',
      colRange: 'Stake per bet (min – max)',
      colStatus: 'Status',
      range: (min, max) => `${min} – ${max}`,
      statusActive: 'Active',
      statusOff: 'Suspended',
      add: 'Add limit',
      edit: 'Edit',
      suspend: 'Suspend',
      reactivate: 'Reactivate',
      empty: 'No limits yet — members cannot play KENO until one is added',
      minLabel: 'Min per bet (THB)',
      maxLabel: 'Max per bet (THB)',
      addTitle: 'Add a limit',
      editTitle: 'Edit limit',
      dialogHint: 'Whole numbers, different from every existing row. A change applies to the next bet straight away',
      invalidRange: 'Min must be a whole number from 1, and max must not be below min',
      saveFailed: 'Could not save',
      savedLimit: 'Limit saved',
      suspendedLimit: 'Limit suspended',
      reactivatedLimit: 'Limit reactivated',
      assignTitle: 'Assign to a master',
      assignHint: 'Pick a master and tick the rows its line may use — the master can only pass on a subset of these to its own agents',
      pickMaster: 'Pick a master',
      noMasters: 'No masters yet',
      partnerTitle: 'Rows in use',
      partnerHint: 'Nothing ticked = every active row is available',
      optionsEmpty: 'Nothing to choose from — the level above has not opened any row',
      nothingChosen: 'Nothing chosen — every active row is available',
      membersSee: 'Members see now',
      blocked: 'The chosen rows leave members nothing to bet at — members on this line cannot play KENO. Change the choice, or clear it to use every row',
      save: 'Save',
      clear: 'Clear choice',
      saved: 'Saved',
      cleared: 'Choice cleared — every permitted row applies',
      loadFailed: 'Could not load',
      agentCardTitle: 'KENO bet limits for this agent',
      agentCardHint: 'Choose the “CHOOSE YOUR BET LIMIT” rows this agent’s members can pick — only rows you are allowed yourself are offered',
    },
    partnerWallet: {
      title: 'Partner wallet backlog',
      subtitle: 'Payouts and refunds owed to members of seamless agents that did not reach the partner, or are still being retried — the member\'s money is not where the system believes it is.',
      filterAttention: 'Needs attention',
      filterFailed: 'Failed',
      filterRetrying: 'Retrying',
      filterAll: 'All',
      summaryFailed: (n) => `Failed ${n}`,
      summaryRetrying: (n) => `Retrying ${n}`,
      colTime: 'Time',
      colKind: 'Type',
      colMember: 'Member / agent',
      colAmount: 'Amount',
      colRef: 'Reference',
      colStatus: 'Status',
      colError: 'Last error',
      kindCredit: 'Payout',
      kindRollback: 'Refund / reversal',
      kindSettle: 'Outcome notice (no money)',
      statusFailed: 'Failed',
      statusRetrying: 'Retrying',
      statusDone: 'Delivered',
      statusCancelled: 'Closed / withdrawn',
      statusPending: 'Queued',
      attempts: (n) => `${n} attempts`,
      nextAttempt: 'Next try',
      empty: 'Nothing outstanding — everything owed to partners has arrived.',
      emptyFiltered: 'Nothing under this filter.',
      retry: 'Send again',
      resolve: 'Close',
      reload: 'Reload',
      drawRef: 'Draw',
      roundRef: 'Round',
      retryTitle: 'Send this instruction to the partner again',
      retryDescription: (kind, amount, member) => `${kind} ${amount} · member ${member} — it is tried at once and its retry budget starts over.`,
      retryConfirm: 'Send again',
      resolveTitle: 'Close — already settled with the partner',
      resolveDescription: (kind, amount, member) => `${kind} ${amount} · member ${member} — once closed it is never sent, and its incident is closed too.`,
      resolveNoteLabel: 'Note — how it was settled with the partner',
      resolveNoteHint: 'e.g. paid the member by hand, ticket TK-123',
      resolveNext: 'Next: request a code',
      resolveConfirm: 'Close',
      doneRetry: 'Sent again',
      doneResolve: 'Closed',
    },
    settlements: {
      title: 'Settlements',
      round: 'Round',
      totalBets: 'Total Bets',
      totalBetAmount: 'Bet Amount',
      totalWinAmount: 'Win Amount',
      totalLossAmount: 'Loss Amount',
      status: 'Status',
      processedAt: 'Processed At',
      errorMessage: 'Error',
      noSettlements: 'No settlements',
    },
    reports: {
      title: 'Reports',
      winLoss: 'Win / Loss Report',
      from: 'From',
      to: 'To',
      agent: 'Agent',
      allAgents: 'All Agents',
      generate: 'Generate Report',
      generating: 'Generating...',
      totalBet: 'Total Bet',
      betVolume: 'Bet Volume',
      totalWin: 'Total Win',
      payout: 'Payout',
      netProfit: 'Net Profit',
      agentNet: 'Agent Net',
      masterNet: 'Master Net',
      adminNet: 'Admin Net',
      agentShare: 'Share',
      masterShare: 'Master Share',
      memberCount: 'Members',
      roundCount: 'Rounds',
      noData: 'Click Search to view the report',
      noResults: 'No data in this period',
      results: 'Results',
      totals: 'Totals',
      exportCsv: 'Export CSV',
      exporting: 'Exporting…',
    },
    agents: {
      title: 'Agents',
      createAgent: 'Create Agent',
      code: 'Code',
      name: 'Name',
      commissionRate: 'Agent Share',
      walletType: 'Wallet Type',
      status: 'Status',
      isActive: 'Active',
      apiKey: 'API Key',
      rotateKey: 'Rotate Key',
      testConnection: 'Test Connection',
      connectionOk: 'Connection OK',
      connectionFail: 'Connection failed',
      noAgents: 'No agents',
      createError: 'Failed to create agent',
      updateError: 'Failed to update agent',
      internal: 'Internal',
      seamless: 'Seamless',
      walletConfig: 'Wallet Configuration',
      walletUrl: 'Agent Wallet URL',
      walletApiKey: 'Agent Wallet API Key',
      walletApiKeyHint: 'sent as X-Api-Key to agent',
      walletHelpSeamless: 'Agent manages member balances via their own wallet API.',
      walletHelpInternal: 'Lotmatic manages balances internally (no external wallet calls).',
      loginAccount: 'Agent login account',
      loginAccountHint: 'The agent signs in to the agent console with this account to see its members, reports and API keys.',
      loginCreatedHint: (host) => `The agent signs in at ${host}`,
      walletOptionalHint: 'Optional at creation — set them later on the agent page. Members of a seamless agent can bet once both URL and API key are set.',
      walletNotSet: 'Wallet URL or API key not set — members of this agent cannot bet yet.',
      walletApiKeyKeep: 'Leave blank to keep the current key',
      walletApiKeySet: (masked) => `Set (${masked})`,
      resetPassword: 'Set new password',
      resetPasswordHint: 'The old password stops working. Tell the agent the new one yourself.',
      newPassword: 'New password',
      resetPasswordDone: 'New password set',
      noLoginAccount: 'This agent has no login account',
      resetPasswordError: 'Could not set the new password',
      showPassword: 'Show password',
      hidePassword: 'Hide password',
    },
    providers: {
      title: 'Providers',
      subtitle: 'Price source configuration',
      code: 'Code',
      type: 'Type',
      symbols: 'Symbols',
      isActive: 'Status',
      toggle: 'Toggle',
      toggleError: 'Failed to toggle provider',
      noProviders: 'No providers',
      enable: 'Enable',
      disable: 'Disable',
      config: 'Config',
      typeCrypto: 'Crypto',
      typeForex: 'Forex',
      typeGold: 'Gold',
      typeStock: 'Stock',
      typeOil: 'Oil',
      typeIndex: 'Index',
      typeCustom: 'Custom',
    },
    audit: {
      title: 'Audit Log',
      actor: 'Actor',
      action: 'Action',
      entity: 'Entity',
      ipAddress: 'IP Address',
      detail: 'Detail',
      noLogs: 'No logs',
      viewDetail: 'View detail',
    },
    docs: {
      title: 'Integration Guide',
      overview: 'Overview',
      integrationFlow: 'Integration Flow',
      walletApi: 'Wallet API — Your server must implement',
      codeExamples: 'Code Examples',
      authSection: 'Authentication',
      endpointsSection: 'Endpoints',
      agentCode: 'Agent Code',
      yourApiKey: 'Your Lotmatic API Key',
      apiBase: 'Lotmatic API Base',
      walletUrl: 'Your Wallet URL',
      overviewText: 'Lotmatic uses a Seamless Wallet model. Your server holds member balances. When a member places a bet or wins, Lotmatic calls your wallet API to debit or credit the member\'s account.',
      authText: 'Every request from Lotmatic includes X-Api-Key: <your_wallet_api_key>. Reject with 401 if the key doesn\'t match.',
      method: 'Method',
      path: 'Path',
      description: 'Description',
      epBalance: 'Get member balance',
      epDebit: 'Deduct bet amount from member',
      epCredit: 'Credit win payout to member',
      epRollback: 'Reverse a debit or credit',
      epSettle: 'Notify the bet outcome (won/lost) for reconciliation only — moves no money',
      balanceQueryParam: 'Query param: memberId — your internal member identifier.',
      debitIdempotent: 'Deduct the bet amount. Must be idempotent on txId.',
      rollbackDesc: 'Reverse a previous debit or credit. Called when settlement fails.',
      settleDesc: 'Notifies the final outcome of a bet (won or lost) so your side can reconcile. This endpoint never moves money — debit/credit already did. Called for every bet, including losses. A slow or unreachable endpoint here never blocks or fails Lotmatic settlement (automatic retries are built in).',
      flowStep0: 'Member registers on your platform — your server calls POST {NEXBIT_API_BASE_URL}/api/external/create-member to obtain a memberId from Lotmatic (once per member; no longer created from the Admin/Agent panel).',
      flowStep1: 'Member clicks "Play" on your platform.',
      flowStep2: 'Your server generates a launchToken (HMAC) itself, then calls POST {NEXBIT_API_BASE_URL}/api/external/launch with the X-Partner-Key header — see how to generate the token below.',
      flowStep3: 'Receive an accessToken + launchUrl in response. Redirect the member to the returned launchUrl (token lifetime follows JWT_EXPIRES_IN).',
      flowStep4: 'Lotmatic validates the token and loads the game for the member.',
      flowStep5: 'When the member bets, Lotmatic calls your /api/wallet/debit.',
      flowStep6: 'On round settlement, Lotmatic calls /api/wallet/credit for winners.',
      flowStep7: 'If an error occurs mid-flow, Lotmatic calls /api/wallet/rollback to reverse the debit.',
      flowStep8: 'After settlement, Lotmatic calls /api/wallet/settle to report the win/loss outcome of every bet (including losses) so you can reconcile.',
      launchTitle: 'Launching a Member (generating the launchToken with HMAC)',
      launchExplain: 'The launchToken is generated by your own server — it is not requested from Lotmatic first. Generate a random nonce, then sign it with HMAC-SHA256 using the Launch Secret Lotmatic gives you (a separate value from your Lotmatic API Key).',
      launchTokenFormat: 'Token format: <nonce>.<hmac>, where hmac = HMAC_SHA256(launchSecret, `${memberId}:${nonce}`) as a hex string.',
      launchSecretLabel: 'Launch Secret',
      launchSecretNote: 'The Launch Secret is separate from your Lotmatic API Key — view or rotate it from the Agent Detail page (shown in full only once, at creation or rotation). Never embed this secret in client-side code — only generate the token on your server.',
      launchGenerateHeading: 'Steps to generate the launchToken',
      launchRequestHeading: 'Exchange it for a Lotmatic session',
      launchResponseHeading: 'Response',
      createMemberTitle: 'Member Provisioning',
      createMemberExplain: 'Every member must be created through this API only — it can no longer be created from the Admin/Agent panel. Send a username and displayName; Lotmatic checks whether this member already exists under your agent account, creates it if not, and returns a unique memberId — store this value for your own reference.',
      createMemberIdempotentNote: 'Idempotent: calling again with the same username returns the same memberId (no duplicate is created) — safe to retry on a network timeout.',
      createMemberRequestHeading: 'Create the member',
      createMemberResponseHeading: 'Response',
      kenoLaunchTitle: 'Launching into KENO',
      kenoLaunchExplain: 'KENO lives on its own site, separate from the lottery, but shares the same members, wallet and Wallet API. Build the launchToken exactly the same way; only call /api/external/keno/launch instead of /api/external/launch, and the returned launchUrl takes the member into KENO. The gameCode in debit/settle is the table code, e.g. keno_earth (keno_jupiter, keno_mars, keno_neptune).',
      drawLotteryTitle: 'Lotteries (government, stock, …)',
      drawLotteryExplain: 'Draw lotteries use the same Wallet API and the same /api/external/launch. gameCode is the lottery code (e.g. government) and roundId is the draw id. The unit of money is the bill — one submit by the member: one bill = one /debit for the total of the bill · a winning bill = one /credit (all winning numbers of that bill together) · the outcome of each bill is reported with /settle.',
      drawLotteryMoneyNote: 'Winnings and refunds are sent asynchronously: we record the instruction in the same transaction as the settlement and deliver it within seconds. If your side does not answer we keep retrying for about 12 hours. Every instruction reuses the same txId, so you must answer a repeat with the original result without debiting or crediting twice. Refunds for a cancelled/skipped draw and the reversal of a payout after a result correction are sent as /rollback, with originalTxId naming the earlier /debit or /credit. Reversing winnings the member has already spent may be refused by your side (insufficient balance); we then open it as an incident for a person to resolve.',
      kenoRefundNote: 'KENO has a full-refund outcome: a Dragon/Tiger bet when the result is a DT tie. Lotmatic calls /settle with result "won" and winAmount equal to the stake (stake back, no profit), so your side needs no new result type.',
    },
    detail: {
      backToAgents: 'Agents',
      agentNotFound: 'Agent not found',
      loadFailed: 'Failed to load agent',
      integrationDocs: 'Integration Docs',
      credentials: 'Credentials',
      agentCode: 'Agent Code',
      apiKeyMasked: 'Lotmatic API Key (masked)',
      newApiKeyNotice: 'New API key — copy now:',
      newApiKeyLabel: 'New Lotmatic API Key',
      rotateApiKey: 'Rotate API Key',
      rotateApiKeyHint: 'Invalidates the current key immediately',
      launchSecretMasked: 'Launch Secret (masked)',
      newLaunchSecretNotice: 'New Launch Secret — copy now:',
      newLaunchSecretLabel: 'New Launch Secret',
      rotateLaunchSecret: 'Rotate Launch Secret',
      rotateLaunchSecretHint: 'Used to sign launchToken — invalidates the current secret immediately, the agent side must be updated too',
      testing: 'Testing…',
      testConnection: 'Test Connection',
      connected: 'Connected',
      connectionFailed: 'Connection failed',
      requestFailed: 'Request failed',
      settings: 'Settings',
      displayName: 'Display Name',
      nameRequired: 'Name is required',
      commissionRate: 'Agent Share (%)',
      commissionRateHint: '(0–100, e.g. 90 = agent gets 90%)',
      commissionRateInvalid: 'Must be 0–100 (e.g. 90 for 90%)',
      status: 'Status',
      activeLabel: 'Active (members can play)',
      walletType: 'Wallet Type',
      agentWalletUrl: 'Agent Wallet URL',
      walletUrlInvalid: 'Must be a valid URL',
      agentWalletApiKey: 'Agent Wallet API Key',
      agentWalletApiKeyHint: '(leave blank to keep current)',
      showKey: 'Show',
      hideKey: 'Hide',
      saveChanges: 'Save Changes',
      saving: 'Saving…',
      saveSuccess: 'Saved successfully',
      saveFailed: 'Save failed',
      copyLabel: 'Copy',
    },
  },
}
